import React, { useState, useEffect, useRef } from 'react';
import { usersAPI, campaignsAPI, campaignProvisionsAPI, ocrAPI, invoiceAPI, certificateAPI, quarterlyProvisionsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { WAHR_LOGO_BASE64, SIGNATURE_BASE64 } from '../utils/certificateImages';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Euro, 
  User,
  Calendar,
  ArrowRightLeft,
  Plus,
  Trash2,
  Users,
  Calculator,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  CheckCircle,
  Circle,
  Loader2,
  RefreshCw,
  Camera,
  Sparkles,
  AlertCircle,
  FileText,
  Download,
  Settings,
  Building,
  CreditCard,
  Award,
  ExternalLink,
  X,
  Share2,
  Copy,
  Check,
  Edit3,
  Mail,
  MapPin,
  UserPlus,
  Pencil,
  Phone,
  Percent,
  CalendarDays
} from 'lucide-react';
import { captureCertificateAsDataUrl, downloadCertificateAsPdf } from '../utils/certificateCapture';

// Certificate Dialog Component - Using Shadcn Dialog
const CertificateDialog = ({ open, onClose, traineeName, traineeId, traineeEmail, campaign, savedCertificate, onSaveCertificate }) => {
  const [notes, setNotes] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [topic, setTopic] = useState('');
  const [selectedRank, setSelectedRank] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [emailSent, setEmailSent] = useState(null);
  const certificateRef = useRef(null);

  // Rangsystem basierend auf Einheiten
  const RANKS = [
    { id: 'SS', title: 'Legende', points: 1000, color: '#FFD700', bgColor: '#FFF9E6' },
    { id: 'S', title: 'Elite', points: 800, color: '#C0C0C0', bgColor: '#F5F5F5' },
    { id: 'A', title: 'Profi', points: 600, color: '#CD7F32', bgColor: '#FDF5E6' },
    { id: 'B', title: 'Aufsteiger', points: 400, color: '#4b95d1', bgColor: '#E6F3FF' },
    { id: 'C', title: 'Starter', points: 200, color: '#6B7280', bgColor: '#F3F4F6' },
  ];

  useEffect(() => {
    if (open) {
      if (savedCertificate) {
        const dates = deriveCampaignDates();
        setNotes(savedCertificate.notes || '');
        setDateFrom(toISODate(savedCertificate.date_from) || dates.from);
        setDateTo(toISODate(savedCertificate.date_to) || dates.to);
        setTopic(savedCertificate.topic || campaign?.name || '');
        setSelectedRank(savedCertificate.rank || '');
        setCertificate({
          allgemeiner_eindruck: savedCertificate.allgemeiner_eindruck,
          teamverhalten: savedCertificate.teamverhalten,
          arbeitsweise: savedCertificate.arbeitsweise,
          leistungsentwicklung: savedCertificate.leistungsentwicklung,
          fazit: savedCertificate.fazit
        });
        setSaved(true);
      } else {
        const dates = deriveCampaignDates();
        setNotes('');
        setCertificate(null);
        setDateFrom(dates.from);
        setDateTo(dates.to);
        setTopic(campaign?.name || '');
        setSelectedRank('');
        setSaved(false);
      }
      setError(null);
      setEmailSent(null);
      setSendEmail(!!traineeEmail);
    }
  }, [open, traineeName, campaign, savedCertificate, traineeEmail]);

  const generateCertificate = async () => {
    if (!notes.trim()) { setError('Bitte Stichpunkte eingeben'); return; }
    setGenerating(true);
    setError(null);
    setSaved(false);
    setEmailSent(null);
    try {
      const result = await certificateAPI.generate({ name: traineeName, dateFrom, dateTo, topic, notes });
      setCertificate(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler bei der Generierung');
    }
    setGenerating(false);
  };

  const saveCertificateData = async () => {
    if (!certificate || !traineeId) return;
    setSaving(true);
    setEmailSent(null);
    try {
      const result = await onSaveCertificate(traineeId, {
        topic, date_from: dateFrom, date_to: dateTo, notes,
        rank: selectedRank,
        allgemeiner_eindruck: certificate.allgemeiner_eindruck,
        teamverhalten: certificate.teamverhalten,
        arbeitsweise: certificate.arbeitsweise,
        leistungsentwicklung: certificate.leistungsentwicklung,
        fazit: certificate.fazit,
        generated_at: new Date().toISOString(),
        text: `${certificate.allgemeiner_eindruck}\n\n${certificate.teamverhalten}\n\n${certificate.arbeitsweise}\n\n${certificate.leistungsentwicklung}\n\n${certificate.fazit}`
      }, sendEmail && !!traineeEmail);
      setSaved(true);
      if (result?.email) {
        setEmailSent(result.email);
      }
    } catch (err) {
      setError('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const getRankData = (rankId) => RANKS.find(r => r.id === rankId);

  const formatDateDE = (d) => {
    if (!d) return '';
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return d;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const toISODate = (d) => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.split('T')[0];
    const m = String(d).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return d;
  };

  const deriveCampaignDates = () => {
    let from = campaign?.start_date || '';
    let to = campaign?.end_date || '';
    if ((!from || !to) && campaign?.id) {
      const m = String(campaign.id).match(/^(\d{4})-kw-?(\d+)-(\d+)$/i);
      if (m) {
        const year = parseInt(m[1]);
        const mondayOf = (week) => {
          const simple = new Date(year, 0, 1 + (week - 1) * 7);
          const dow = simple.getDay();
          const mon = new Date(simple);
          if (dow <= 4) mon.setDate(simple.getDate() - simple.getDay() + 1);
          else mon.setDate(simple.getDate() + 8 - simple.getDay());
          return mon;
        };
        const s = mondayOf(parseInt(m[2]));
        const e = mondayOf(parseInt(m[3]));
        e.setDate(e.getDate() + 6);
        from = s.toISOString().split('T')[0];
        to = e.toISOString().split('T')[0];
      }
    }
    return { from: toISODate(from), to: toISODate(to) };
  };

  const downloadAsImage = async () => {
    if (!certificateRef.current) return;
    try {
      const dataUrl = await captureCertificateAsDataUrl(certificateRef.current);
      const fileName = `Zertifikat_${traineeName.replace(/\s+/g, '_')}.png`;
      
      // Use Blob URL instead of document.write for security (XSS prevention)
      const htmlContent = `<!DOCTYPE html>
        <html>
        <head>
          <title>${fileName}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body { margin: 0; padding: 20px; background: #1c2e52; text-align: center; font-family: -apple-system, sans-serif; min-height: 100vh; }
            .info { background: white; border-radius: 12px; padding: 16px; margin-bottom: 16px; max-width: 600px; margin-left: auto; margin-right: auto; }
            .info p { margin: 8px 0; color: #333; font-size: 14px; }
            img { max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
            .btn { display: inline-block; background: #4b95d1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 8px 4px; font-weight: bold; border: none; cursor: pointer; font-size: 16px; }
          </style>
        </head>
        <body>
          <div class="info">
            <p><strong>📱 So speichern Sie das Bild:</strong></p>
            <p>iPhone/iPad: Lange auf das Bild drücken → "Zu Fotos hinzufügen"</p>
            <p>Android: Lange auf das Bild drücken → "Bild herunterladen"</p>
            <p>Desktop: Rechtsklick → "Bild speichern unter"</p>
          </div>
          <img src="${dataUrl}" alt="Zertifikat" />
        </body>
        </html>`;
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        alert('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
      }
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) { 
      console.error('Download error:', err);
      setError('Fehler beim Erstellen des Bildes');
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Zertifikat für ${traineeName}\n${topic || ''}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const copyText = () => {
    if (!certificate) return;
    navigator.clipboard.writeText(`ZERTIFIKAT: ${traineeName}\n\n${certificate.allgemeiner_eindruck}\n\n${certificate.teamverhalten}\n\n${certificate.arbeitsweise}\n\n${certificate.leistungsentwicklung}\n\n${certificate.fazit}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-[#4b95d1]" />
            Zertifikat für {traineeName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Form */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Von</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8" />
                </div>
                <div>
                  <Label className="text-xs">Bis</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Thema</Label>
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="z.B. Vertriebsschulung" className="h-8" />
              </div>
              
              {/* Rang Auswahl */}
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Award className="w-3 h-3 text-amber-500" />
                  Erreichter Rang
                </Label>
                <Select value={selectedRank} onValueChange={setSelectedRank}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Rang auswählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {RANKS.map(rank => (
                      <SelectItem key={rank.id} value={rank.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-bold" style={{ color: rank.color }}>Rang {rank.id}</span>
                          <span className="text-gray-600">- {rank.title}</span>
                          <span className="text-xs text-gray-400">({rank.points} Pkt)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs">Stichpunkte *</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="- Sehr engagiert&#10;- Gute Kommunikation" className="min-h-[150px]" />
              </div>
              {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">{error}</div>}
              <div className="flex gap-2">
                <Button onClick={generateCertificate} disabled={generating || !notes.trim()} className="flex-1 bg-[#1c2e52]">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Generieren
                </Button>
                <Button variant="outline" onClick={() => { setNotes(''); setCertificate(null); setSaved(false); }}>Reset</Button>
              </div>
            </div>

            {/* Right: Preview */}
            <div className="space-y-3">
              {certificate ? (
                <>
                  <div className="flex gap-2 flex-wrap items-center">
                    <Button size="sm" onClick={saveCertificateData} disabled={saving || saved} className={saved ? 'bg-gray-400' : 'bg-[#1c2e52]'}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                      <span className="ml-1">{saved ? 'Gespeichert' : 'Speichern'}</span>
                    </Button>
                    <Button size="sm" onClick={downloadAsImage} className="bg-green-600 hover:bg-green-700">
                      <Download className="w-4 h-4 mr-1" />PNG
                    </Button>
                    <Button size="sm" onClick={() => downloadCertificateAsPdf(certificateRef.current, `Zertifikat_${traineeName.replace(/\s+/g, '_')}.pdf`)} className="bg-red-600 hover:bg-red-700" data-testid="admin-cert-download-pdf">
                      <Download className="w-4 h-4 mr-1" />PDF (A4)
                    </Button>
                    <Button size="sm" onClick={shareViaWhatsApp} className="bg-[#25D366]">
                      <Share2 className="w-4 h-4 mr-1" />Teilen
                    </Button>
                    <Button size="sm" variant="outline" onClick={copyText}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  {/* Email notification option */}
                  {traineeEmail && !saved && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <Checkbox 
                        id="sendEmail" 
                        checked={sendEmail} 
                        onCheckedChange={setSendEmail}
                        className="border-blue-400 data-[state=checked]:bg-blue-500"
                      />
                      <label htmlFor="sendEmail" className="text-blue-700 cursor-pointer flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        E-Mail an {traineeEmail} senden
                      </label>
                    </div>
                  )}
                  
                  {/* Email status */}
                  {emailSent && (
                    <div className={`flex items-center gap-2 p-2 rounded text-xs ${emailSent.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
                      <Mail className="w-3 h-3" />
                      {emailSent.message}
                    </div>
                  )}
                  
                  {!traineeEmail && !saved && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                      <AlertCircle className="w-3 h-3" />
                      Keine E-Mail für Trainee hinterlegt
                    </div>
                  )}
                  
                  <div 
                    ref={certificateRef}
                    className="bg-white rounded-lg border-[6px] border-double border-[#1c2e52] relative"
                    style={{ 
                      backgroundImage: `url(${WAHR_LOGO_BASE64})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: '35%',
                      backgroundBlendMode: 'overlay',
                      width: '480px',
                      minWidth: '480px',
                      minHeight: '679px',
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '24px 28px'
                    }}
                  >
                    <div className="absolute inset-0 bg-white/90 rounded-lg" />
                    <div className="relative z-10 flex flex-col flex-1">
                      {/* Rang Badge - oben rechts */}
                      {selectedRank && getRankData(selectedRank) && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '-4px', 
                          right: '-4px', 
                          zIndex: 2
                        }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: getRankData(selectedRank).bgColor,
                            border: `2px solid ${getRankData(selectedRank).color}`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            textAlign: 'center',
                            overflow: 'hidden',
                            boxSizing: 'border-box'
                          }}>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: getRankData(selectedRank).color, lineHeight: '24px', marginTop: '6px' }}>{selectedRank}</div>
                            <div style={{ fontSize: '6px', color: getRankData(selectedRank).color, fontWeight: '600', lineHeight: '8px' }}>{getRankData(selectedRank).title}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Header - kompakt */}
                      <div className="flex justify-center mb-3">
                        <img src={WAHR_LOGO_BASE64} alt="WAHR Logo" className="h-10 object-contain" />
                      </div>
                      <div className="text-center mb-4">
                        <h1 className="text-xl font-bold text-[#1c2e52] mb-1">SCHULUNGSZERTIFIKAT</h1>
                        <p className="text-xs text-[#3e4350]">WAHR - Privatinstitut für Dialog & Markenkompetenz</p>
                      </div>

                      {/* Name - kompakt */}
                      <div className="text-center mb-4">
                        <p className="text-sm text-[#3e4350]">Hiermit bestätigen wir, dass</p>
                        <p className="text-lg font-bold text-[#1c2e52] my-2">{traineeName}</p>
                        <p className="text-xs text-[#3e4350]">
                          erfolgreich an der Schulung{dateFrom && dateTo ? ` vom ${formatDateDE(dateFrom)} bis ${formatDateDE(dateTo)}` : ''} teilgenommen hat.
                        </p>
                        {selectedRank && getRankData(selectedRank) && (
                          <span style={{ 
                            fontSize: '10px', 
                            fontWeight: 'bold',
                            color: getRankData(selectedRank).color, 
                            marginTop: '6px',
                            padding: '2px 10px',
                            backgroundColor: getRankData(selectedRank).bgColor,
                            borderRadius: '3px',
                            display: 'inline-block'
                          }}>
                            Rang {selectedRank} – {getRankData(selectedRank).title}
                          </span>
                        )}
                      </div>

                      {/* Content Sections - 2 Spalten für Kompaktheit */}
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div className="bg-[#e6d7bf]/20 p-2 rounded">
                          <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Allgemeiner Eindruck</h3>
                          <p className="text-[#3e4350] text-[10px] leading-tight">{certificate.allgemeiner_eindruck}</p>
                        </div>
                        <div className="bg-[#4b95d1]/10 p-2 rounded">
                          <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Teamverhalten</h3>
                          <p className="text-[#3e4350] text-[10px] leading-tight">{certificate.teamverhalten}</p>
                        </div>
                        <div className="bg-[#4b95d1]/10 p-2 rounded">
                          <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Arbeitsweise</h3>
                          <p className="text-[#3e4350] text-[10px] leading-tight">{certificate.arbeitsweise}</p>
                        </div>
                        <div className="bg-[#e6d7bf]/20 p-2 rounded">
                          <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Leistungsentwicklung</h3>
                          <p className="text-[#3e4350] text-[10px] leading-tight">{certificate.leistungsentwicklung}</p>
                        </div>
                      </div>
                      
                      {/* Fazit - volle Breite */}
                      <div className="bg-[#1c2e52]/10 p-2 rounded mb-3">
                        <h3 className="font-bold text-[#1c2e52] text-xs mb-1">Fazit</h3>
                        <p className="text-[#3e4350] text-[10px] leading-tight">{certificate.fazit}</p>
                      </div>

                      {/* Signature - kompakt */}
                      <div className="flex justify-between items-end pt-2 border-t border-[#1c2e52] mt-auto">
                        <div className="text-center">
                          <p className="text-[10px] text-[#3e4350]">Monheim am Rhein, {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                          <p className="text-[8px] text-[#3e4350]">Ort, Datum</p>
                        </div>
                        <div className="text-center">
                          <img src={SIGNATURE_BASE64} alt="Unterschrift" className="h-8 mx-auto" />
                          <div className="border-t border-[#1c2e52] pt-0.5 px-4">
                            <p className="text-[8px] text-[#3e4350]">Schulungsleitung</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed rounded-lg p-8 text-center">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Stichpunkte eingeben und &quot;Generieren&quot; klicken</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Week Block Component
const WeekBlock = ({ 
  weekNum, 
  trainees, 
  totalUnits,
  onAddTrainee,
  onDeleteTrainee,
  onTogglePaid,
  onEditTrainee, // New prop for editing trainee details
  ocrResult,
  ocrLoading,
  ocrError,
  onFileSelect,
  onAddOcrTrainee,
  onAddAllOcrTrainees,
  onUpdateOcrUnits,
  onClearOcr,
  onClearError,
  fileInputRef,
  saving,
  formatCurrency,
  formatNumber,
  campaign,
  onOpenCertificate,
  multipliers // Custom multipliers { multiplier_w12, multiplier_w3, mwst_rate }
}) => {
  const [newName, setNewName] = useState('');
  const [newUnits, setNewUnits] = useState('');
  
  const handleAdd = () => {
    if (newName.trim() && newUnits) {
      onAddTrainee(newName.trim(), parseFloat(newUnits));
      setNewName('');
      setNewUnits('');
    }
  };
  
  const bgColor = weekNum === 1 ? 'border-blue-200 bg-blue-50/30' : 'border-indigo-200 bg-indigo-50/30';
  const headerColor = weekNum === 1 ? 'text-blue-700' : 'text-indigo-700';
  const accentColor = weekNum === 1 ? 'blue' : 'indigo';
  
  return (
    <Card className={bgColor}>
      <CardHeader className="pb-2">
        <CardTitle className={`text-base flex items-center justify-between ${headerColor}`}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Woche {weekNum} ({trainees.length} Trainees, {formatNumber(totalUnits)} E)
          </div>
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFileSelect}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid={`ocr-file-input-w${weekNum}`}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={ocrLoading}
              className={`text-xs border-${accentColor}-400 text-${accentColor}-600 hover:bg-${accentColor}-50`}
              data-testid={`ocr-upload-btn-w${weekNum}`}
            >
              {ocrLoading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Camera className="w-3 h-3 mr-1" />
              )}
              Foto scannen
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* OCR Results */}
        {ocrResult && ocrResult.trainees && ocrResult.trainees.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-purple-700">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {ocrResult.trainees.length} erkannt
                </span>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  onClick={onAddAllOcrTrainees}
                  disabled={saving}
                  className="text-xs bg-purple-600 hover:bg-purple-700"
                >
                  Alle hinzufügen
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearOcr}
                  className="text-xs text-purple-600"
                >
                  ×
                </Button>
              </div>
            </div>
            <div className="space-y-1 max-h-[100px] overflow-y-auto">
              {ocrResult.trainees.map((trainee, idx) => (
                <div key={`ocr-${trainee.name}-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border border-purple-100">
                  <span className="flex-1 text-sm truncate">{trainee.name}</span>
                  <Input
                    type="number"
                    value={trainee.units}
                    onChange={(e) => onUpdateOcrUnits(idx, e.target.value)}
                    className="w-16 h-7 text-xs border-purple-200 text-center"
                  />
                  <span className="text-xs text-purple-500">E</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAddOcrTrainee(trainee)}
                    disabled={saving}
                    className="h-6 px-2 text-purple-600 hover:bg-purple-50"
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* OCR Error */}
        {ocrError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700 flex-1">{ocrError}</span>
            <Button variant="ghost" size="sm" onClick={onClearError} className="h-6 px-2 text-red-500">×</Button>
          </div>
        )}
        
        {/* Add Trainee Form */}
        <div className="flex gap-2">
          <Input
            placeholder="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className={`flex-1 border-${accentColor}-200`}
            data-testid={`trainee-name-w${weekNum}`}
          />
          <Input
            type="number"
            placeholder="Einheiten"
            value={newUnits}
            onChange={(e) => setNewUnits(e.target.value)}
            className={`w-20 border-${accentColor}-200`}
            data-testid={`trainee-units-w${weekNum}`}
          />
          <Button 
            onClick={handleAdd}
            size="sm"
            className={`bg-${accentColor}-500 hover:bg-${accentColor}-600`}
            disabled={!newName.trim() || !newUnits || saving}
            data-testid={`add-trainee-w${weekNum}`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Trainees List */}
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {trainees.map((trainee) => (
            <div 
              key={trainee.id}
              className={`flex items-center gap-2 p-2 rounded border ${
                trainee.fee_received ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
              }`}
            >
              <Checkbox
                checked={trainee.fee_received}
                onCheckedChange={(checked) => onTogglePaid(trainee.id, checked)}
                className="border-green-500 data-[state=checked]:bg-green-500"
              />
              <div className="flex-1 min-w-0">
                <span className={`text-sm truncate block ${trainee.fee_received ? 'line-through text-gray-400' : ''}`}>
                  {trainee.name}
                </span>
                {(trainee.email || trainee.address) && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    {trainee.email && <Mail className="w-3 h-3" />}
                    {trainee.address && <MapPin className="w-3 h-3" />}
                  </div>
                )}
              </div>
              <Badge variant="outline" className="text-xs">
                {formatNumber(weekNum === 1 ? trainee.units_week1 : trainee.units_week2)} E
              </Badge>
              <span className="text-sm font-medium text-blue-600">
                {formatCurrency((weekNum === 1 ? trainee.units_week1 : trainee.units_week2) * (multipliers?.multiplier_w12 ?? 1.4) * (1 + (multipliers?.mwst_rate ?? 0.19)))}
              </span>
              <button
                onClick={() => onOpenCertificate(trainee)}
                className={`h-6 w-6 p-0 inline-flex items-center justify-center rounded transition-colors ${
                  trainee.certificate 
                    ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' 
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
                title={trainee.certificate ? "Zertifikat anzeigen" : "Zertifikat erstellen"}
              >
                <Award className="w-3 h-3" />
              </button>
              <button
                onClick={() => onEditTrainee(trainee)}
                className="h-6 w-6 p-0 inline-flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 transition-colors"
                title="Trainee bearbeiten"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteTrainee(trainee.id)}
                className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
          {trainees.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Keine Trainees für Woche {weekNum}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ProvisionManagerExtended = ({ 
  open, 
  onClose,
  initialTrainer = '',
  initialCampaign = '',
  initialQuarter = null,
  initialYear = 2025,
  readOnly = false,
  lockedTrainer = null
}) => {
  const [trainers, setTrainers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(initialTrainer);
  const [selectedCampaign, setSelectedCampaign] = useState(initialCampaign);
  const [selectedYear, setSelectedYear] = useState(String(initialYear));
  const [showMoveKwDialog, setShowMoveKwDialog] = useState(false);
  const [moveStartWeek, setMoveStartWeek] = useState('');
  const [moveEndWeek, setMoveEndWeek] = useState('');
  const [movingKw, setMovingKw] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState(initialQuarter ? String(initialQuarter) : '');
  const [provision, setProvision] = useState(null);
  const [quarterlyProvision, setQuarterlyProvision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Update state when props change
  useEffect(() => {
    if (open) {
      if (initialTrainer) setSelectedTrainer(initialTrainer);
      if (initialCampaign) setSelectedCampaign(initialCampaign);
      if (initialQuarter) setSelectedQuarter(String(initialQuarter));
      if (initialYear) setSelectedYear(String(initialYear));
    }
  }, [open, initialTrainer, initialCampaign, initialQuarter, initialYear]);
  
  // Week 1 OCR state
  const [ocrLoadingW1, setOcrLoadingW1] = useState(false);
  const [ocrResultW1, setOcrResultW1] = useState(null);
  const [ocrErrorW1, setOcrErrorW1] = useState(null);
  const fileInputRefW1 = useRef(null);
  
  // Week 2 OCR state
  const [ocrLoadingW2, setOcrLoadingW2] = useState(false);
  const [ocrResultW2, setOcrResultW2] = useState(null);
  const [ocrErrorW2, setOcrErrorW2] = useState(null);
  const fileInputRefW2 = useRef(null);
  
  // Week 3 (Neuwerber) OCR state - now for quarterly
  const [ocrLoadingW3, setOcrLoadingW3] = useState(false);
  const [ocrResultW3, setOcrResultW3] = useState(null);
  const [ocrErrorW3, setOcrErrorW3] = useState(null);
  const fileInputRefW3 = useRef(null);
  
  // Advance/Payout state
  const [newAdvanceAmount, setNewAdvanceAmount] = useState('');
  const [newAdvanceNote, setNewAdvanceNote] = useState('');
  const [newPayoutAmount, setNewPayoutAmount] = useState('');
  const [newPayoutNote, setNewPayoutNote] = useState('');
  
  // Profile editor state
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  
  // New trainer dialog state
  const [showNewTrainerDialog, setShowNewTrainerDialog] = useState(false);
  const [newTrainerData, setNewTrainerData] = useState({ name: '', email: '', password: '' });
  const [creatingTrainer, setCreatingTrainer] = useState(false);
  const [trainerError, setTrainerError] = useState('');
  
  // Invoice state
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  
  // Certificate dialog state
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [certificateTrainee, setCertificateTrainee] = useState(null);
  
  // Trainee editor state
  const [showTraineeEditor, setShowTraineeEditor] = useState(false);
  const [editingTrainee, setEditingTrainee] = useState(null);
  const [savingTrainee, setSavingTrainee] = useState(false);
  
  // Add trainee in advance state
  const [showAddTraineeDialog, setShowAddTraineeDialog] = useState(false);
  const [newTraineeData, setNewTraineeData] = useState({ name: '', email: '', phone: '', address: '' });
  const [addingTrainee, setAddingTrainee] = useState(false);
  
  // New trainee form state (for simplified combined input)
  const [newTraineeName, setNewTraineeName] = useState('');
  const [newTraineeUnits, setNewTraineeUnits] = useState('');
  
  // Quarterly Neuwerber multipliers
  const [quarterlyMultipliers, setQuarterlyMultipliers] = useState({ multiplier: 1.0, mwst_rate: 0.19, ug_factor: 2.0, ug_mwst_rate: 0.19 });
  const [showQuarterlyMultDialog, setShowQuarterlyMultDialog] = useState(false);
  const [savingQuarterlyMult, setSavingQuarterlyMult] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTargetId, setShareTargetId] = useState('');
  const [sharing, setSharing] = useState(false);
  const [saveMwstAsDefault, setSaveMwstAsDefault] = useState(false);
  
  // Migration state
  const [migrating, setMigrating] = useState(false);
  
  const handleMigrateCampaignTrainees = async () => {
    if (!window.confirm('Alle bestehenden Trainees aus Kampagnen werden in die entsprechenden Quartale übernommen. Fortfahren?')) {
      return;
    }
    
    setMigrating(true);
    try {
      const result = await quarterlyProvisionsAPI.migrateFromCampaigns();
      alert(`Migration abgeschlossen:\n${result.migrated} Trainees migriert\n${result.skipped} übersprungen (bereits vorhanden)`);
      // Reload quarterly provision to show migrated trainees
      await loadQuarterlyProvision();
    } catch (error) {
      console.error('Migration error:', error);
      alert('Fehler bei der Migration: ' + (error.response?.data?.detail || error.message));
    }
    setMigrating(false);
  };
  
  const openCertificateDialog = (trainee) => {
    setCertificateTrainee(trainee);
    setShowCertificateDialog(true);
  };
  
  const saveCertificateForTrainee = async (traineeId, certificateData, sendEmail = true) => {
    let response;
    if (certificateTrainee?.isQuarterly) {
      if (readOnly) {
        response = await quarterlyProvisionsAPI.updateMyCertificate(parseInt(selectedYear), parseInt(selectedQuarter), traineeId, certificateData);
      } else {
        response = await quarterlyProvisionsAPI.updateTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), traineeId, { certificate: certificateData });
      }
      await loadQuarterlyProvision();
      return response;
    }
    if (readOnly) {
      response = await campaignProvisionsAPI.updateMyCertificate(selectedCampaign, traineeId, certificateData, sendEmail);
    } else {
      response = await campaignProvisionsAPI.updateTrainee(
        selectedTrainer,
        selectedCampaign,
        traineeId,
        { certificate: certificateData, send_email: sendEmail }
      );
    }
    await loadProvision();
    return response;
  };
  
  const openTraineeEditor = (trainee) => {
    setEditingTrainee({...trainee});
    setShowTraineeEditor(true);
  };
  
  const saveTraineeDetails = async () => {
    if (!editingTrainee) return;
    setSavingTrainee(true);
    try {
      await campaignProvisionsAPI.updateTrainee(
        selectedTrainer,
        selectedCampaign,
        editingTrainee.id,
        {
          name: editingTrainee.name,
          email: editingTrainee.email || '',
          phone: editingTrainee.phone || '',
          address: editingTrainee.address || ''
        }
      );
      await loadProvision();
      setShowTraineeEditor(false);
      setEditingTrainee(null);
    } catch (error) {
      console.error('Error saving trainee:', error);
    }
    setSavingTrainee(false);
  };

  useEffect(() => {
    if (open) loadInitialData();
  }, [open, selectedYear]);

  useEffect(() => {
    if (selectedTrainer && selectedCampaign) loadProvision();
  }, [selectedTrainer, selectedCampaign]);
  
  useEffect(() => {
    // Load profile when trainer is selected
    if (selectedTrainer) {
      const trainer = trainers.find(t => t.id === selectedTrainer);
      if (trainer) {
        setProfileData({
          name: trainer.name || '',
          company_name: trainer.profile?.company_name || '',
          address: trainer.profile?.address || '',
          zip_code: trainer.profile?.zip_code || '',
          city: trainer.profile?.city || '',
          tax_number: trainer.profile?.tax_number || '',
          iban: trainer.profile?.iban || '',
          bic: trainer.profile?.bic || ''
        });
      }
    }
  }, [selectedTrainer, trainers]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      if (readOnly && lockedTrainer) {
        const campaignsData = await campaignsAPI.getAll();
        setTrainers([lockedTrainer]);
        setCampaigns(campaignsData.filter(c => c.year === parseInt(selectedYear)));
        setLoading(false);
        return;
      }
      const [trainersData, campaignsData] = await Promise.all([
        usersAPI.getAll(),
        campaignsAPI.getAll()
      ]);
      setTrainers(trainersData);
      const filtered = campaignsData.filter(c => c.year === parseInt(selectedYear));
      setCampaigns(filtered);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    const camp = campaigns.find(c => c.id === selectedCampaign);
    if (!window.confirm(`Kampagne "${camp?.name || selectedCampaign}" wirklich löschen?\n\nHinweis: Kampagnen mit vorhandenen Provisionen können nicht gelöscht werden.`)) return;
    try {
      await campaignsAPI.delete(selectedCampaign);
      setSelectedCampaign('');
      const campaignsData = await campaignsAPI.getAll();
      setCampaigns(campaignsData.filter(c => c.year === parseInt(selectedYear)));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Löschen der Kampagne');
    }
  };

  const handleMoveKw = async () => {
    const start = parseInt(moveStartWeek);
    const end = parseInt(moveEndWeek);
    if (!selectedTrainer || !selectedCampaign) return;
    if (isNaN(start) || isNaN(end) || start < 1 || end > 53 || end <= start) {
      alert('Bitte gültige KWs eingeben (Start < Ende, 1-53)');
      return;
    }
    setMovingKw(true);
    try {
      await campaignsAPI.moveTrainerProvision(selectedTrainer, selectedCampaign, start, end, parseInt(selectedYear));
      const newId = `${selectedYear}-kw${start}-${end}`;
      const campaignsData = await campaignsAPI.getAll();
      setCampaigns(campaignsData.filter(c => c.year === parseInt(selectedYear)));
      setSelectedCampaign(newId);
      setShowMoveKwDialog(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Verschieben der Kampagne');
    }
    setMovingKw(false);
  };

  const loadProvision = async () => {
    if (!selectedTrainer || !selectedCampaign) return;
    try {
      const data = await campaignProvisionsAPI.get(selectedTrainer, selectedCampaign);
      setProvision(data);
      if (readOnly) {
        setMultipliers(prev => ({
          ...prev,
          multiplier_w12: data.multiplier_w12 !== undefined && data.multiplier_w12 !== null ? data.multiplier_w12 : prev.multiplier_w12,
          mwst_rate: data.mwst_rate !== undefined && data.mwst_rate !== null ? data.mwst_rate : prev.mwst_rate,
          ug_factor: data.ug_factor !== undefined && data.ug_factor !== null ? data.ug_factor : prev.ug_factor,
          ug_mwst_rate: data.ug_mwst_rate !== undefined && data.ug_mwst_rate !== null ? data.ug_mwst_rate : prev.ug_mwst_rate
        }));
      }
    } catch (error) {
      setProvision({ trainees: [], advances: [], payouts: [], total_units: 0, total_provision_netto: 0, total_provision_brutto: 0, total_advances: 0, total_payouts: 0, remaining_amount: 0 });
    }
  };

  // Load quarterly provision for Neuwerber
  const loadQuarterlyProvision = async () => {
    if (!selectedTrainer || !selectedQuarter) {
      setQuarterlyProvision(null);
      return;
    }
    try {
      const data = readOnly
        ? await quarterlyProvisionsAPI.getMine(parseInt(selectedYear), parseInt(selectedQuarter))
        : await quarterlyProvisionsAPI.get(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter));
      setQuarterlyProvision(data);
      setQuarterlyMultipliers({
        multiplier: data.multiplier ?? 1.0,
        mwst_rate: data.mwst_rate ?? 0.19,
        ug_factor: data.ug_factor ?? 2.0,
        ug_mwst_rate: data.ug_mwst_rate ?? 0.19
      });
    } catch (error) {
      setQuarterlyProvision({ trainees: [], multiplier: 1.0, mwst_rate: 0.19, total_units: 0, total_provision_netto: 0, total_provision_brutto: 0 });
    }
  };

  // Load quarterly provision when quarter or trainer changes
  useEffect(() => {
    if (selectedTrainer && selectedQuarter) {
      loadQuarterlyProvision();
    }
  }, [selectedTrainer, selectedQuarter, selectedYear]);

  const formatCurrency = (value) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
  
  const formatNumber = (value) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

  // Add trainee for specific week (now only 1 or 2 - W3 is separate)
  const addTraineeForWeek = async (week, name, units) => {
    setSaving(true);
    try {
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name,
        units_week1: week === 1 ? units : 0,
        units_week2: week === 2 ? units : 0,
        units_week3: 0, // No longer used in campaign provisions
        fee_received: false
      });
      await loadProvision();
    } catch (error) {
      console.error('Error adding trainee:', error);
    }
    setSaving(false);
  };

  // Add quarterly Neuwerber trainee
  const addQuarterlyTrainee = async (name, units) => {
    if (!selectedTrainer || !selectedQuarter) return;
    setSaving(true);
    try {
      await quarterlyProvisionsAPI.addTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), {
        name,
        units,
        fee_received: false
      });
      await loadQuarterlyProvision();
    } catch (error) {
      console.error('Error adding quarterly trainee:', error);
    }
    setSaving(false);
  };

  // Update quarterly trainee fee status
  const updateQuarterlyTraineeFeeStatus = async (traineeId, feeReceived) => {
    if (!selectedTrainer || !selectedQuarter) return;
    setSaving(true);
    try {
      await quarterlyProvisionsAPI.updateTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), traineeId, { fee_received: feeReceived });
      await loadQuarterlyProvision();
    } catch (error) {
      console.error('Error updating quarterly trainee:', error);
    }
    setSaving(false);
  };

  // Delete quarterly trainee
  const deleteQuarterlyTrainee = async (traineeId) => {
    if (!selectedTrainer || !selectedQuarter) return;
    setSaving(true);
    try {
      await quarterlyProvisionsAPI.deleteTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), traineeId);
      await loadQuarterlyProvision();
    } catch (error) {
      console.error('Error deleting quarterly trainee:', error);
    }
    setSaving(false);
  };

  // Save certificate for quarterly trainee
  const saveQuarterlyCertificate = async (traineeId, certificateData) => {
    if (!selectedTrainer || !selectedQuarter) return;
    await quarterlyProvisionsAPI.updateTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), traineeId, { certificate: certificateData });
    await loadQuarterlyProvision();
  };

  const updateTraineeFeeStatus = async (traineeId, feeReceived) => {
    setSaving(true);
    try {
      await campaignProvisionsAPI.updateTrainee(selectedTrainer, selectedCampaign, traineeId, { fee_received: feeReceived });
      await loadProvision();
    } catch (error) {
      console.error('Error updating trainee:', error);
    }
    setSaving(false);
  };

  const deleteTrainee = async (traineeId) => {
    setSaving(true);
    try {
      await campaignProvisionsAPI.deleteTrainee(selectedTrainer, selectedCampaign, traineeId);
      await loadProvision();
    } catch (error) {
      console.error('Error deleting trainee:', error);
    }
    setSaving(false);
  };

  // OCR handlers for Week 1
  const handleFileSelectW1 = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setOcrErrorW1('Nur JPEG, PNG oder WEBP erlaubt');
      return;
    }
    setOcrLoadingW1(true);
    setOcrErrorW1(null);
    try {
      const result = await ocrAPI.extractTrainees(file);
      if (result.trainees) {
        result.trainees = result.trainees.map(t => ({ ...t, units: t.units_week1 || t.units || 0 }));
      }
      setOcrResultW1(result);
    } catch (error) {
      setOcrErrorW1(error.response?.data?.detail || 'Fehler');
    }
    setOcrLoadingW1(false);
    if (fileInputRefW1.current) fileInputRefW1.current.value = '';
  };

  const addOcrTraineeW1 = async (trainee) => {
    await addTraineeForWeek(1, trainee.name, trainee.units);
    setOcrResultW1(prev => ({ ...prev, trainees: prev.trainees.filter(t => t.name !== trainee.name) }));
  };

  const addAllOcrTraineesW1 = async () => {
    if (!ocrResultW1?.trainees?.length) return;
    setSaving(true);
    for (const t of ocrResultW1.trainees) {
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name: t.name, units_week1: t.units, units_week2: 0, units_week3: 0, fee_received: false
      });
    }
    await loadProvision();
    setOcrResultW1(null);
    setSaving(false);
  };

  // OCR handlers for Week 2
  const handleFileSelectW2 = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setOcrErrorW2('Nur JPEG, PNG oder WEBP erlaubt');
      return;
    }
    setOcrLoadingW2(true);
    setOcrErrorW2(null);
    try {
      const result = await ocrAPI.extractTrainees(file);
      if (result.trainees) {
        result.trainees = result.trainees.map(t => ({ ...t, units: t.units_week1 || t.units || 0 }));
      }
      setOcrResultW2(result);
    } catch (error) {
      setOcrErrorW2(error.response?.data?.detail || 'Fehler');
    }
    setOcrLoadingW2(false);
    if (fileInputRefW2.current) fileInputRefW2.current.value = '';
  };

  const addOcrTraineeW2 = async (trainee) => {
    await addTraineeForWeek(2, trainee.name, trainee.units);
    setOcrResultW2(prev => ({ ...prev, trainees: prev.trainees.filter(t => t.name !== trainee.name) }));
  };

  const addAllOcrTraineesW2 = async () => {
    if (!ocrResultW2?.trainees?.length) return;
    setSaving(true);
    for (const t of ocrResultW2.trainees) {
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name: t.name, units_week1: 0, units_week2: t.units, units_week3: 0, fee_received: false
      });
    }
    await loadProvision();
    setOcrResultW2(null);
    setSaving(false);
  };

  // OCR handlers for Week 3 (Neuwerber)
  const handleFileSelectW3 = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setOcrErrorW3('Nur JPEG, PNG oder WEBP erlaubt');
      return;
    }
    setOcrLoadingW3(true);
    setOcrErrorW3(null);
    try {
      const result = await ocrAPI.extractTrainees(file);
      if (result.trainees) {
        result.trainees = result.trainees.map(t => ({ ...t, units: t.units_week1 || t.units || 0 }));
      }
      setOcrResultW3(result);
    } catch (error) {
      setOcrErrorW3(error.response?.data?.detail || 'Fehler');
    }
    setOcrLoadingW3(false);
    if (fileInputRefW3.current) fileInputRefW3.current.value = '';
  };

  const addOcrTraineeW3 = async (trainee) => {
    await addTraineeForWeek(3, trainee.name, trainee.units);
    setOcrResultW3(prev => ({ ...prev, trainees: prev.trainees.filter(t => t.name !== trainee.name) }));
  };

  const addAllOcrTraineesW3 = async () => {
    if (!ocrResultW3?.trainees?.length) return;
    setSaving(true);
    for (const t of ocrResultW3.trainees) {
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name: t.name, units_week1: 0, units_week2: 0, units_week3: t.units, fee_received: false
      });
    }
    await loadProvision();
    setOcrResultW3(null);
    setSaving(false);
  };

  // Multiplier settings
  const [showMultiplierDialog, setShowMultiplierDialog] = useState(false);
  const [multipliers, setMultipliers] = useState({ multiplier_w12: 1.4, multiplier_w3: 1.0, mwst_rate: 0.19, ug_factor: 2.0, ug_mwst_rate: 0.19 });
  const [savingMultipliers, setSavingMultipliers] = useState(false);

  const loadMultipliers = async () => {
    if (!selectedTrainer || !selectedCampaign || readOnly) return;
    try {
      const data = await campaignProvisionsAPI.getMultipliers(selectedTrainer, selectedCampaign);
      setMultipliers(data);
    } catch (error) {
      console.error('Error loading multipliers:', error);
    }
  };

  const saveMultipliers = async () => {
    if (!selectedTrainer || !selectedCampaign) return;
    setSavingMultipliers(true);
    try {
      await campaignProvisionsAPI.updateMultipliers(selectedTrainer, selectedCampaign, multipliers);
      if (saveMwstAsDefault) {
        await usersAPI.updateSettings(selectedTrainer, { default_mwst_rate: multipliers.mwst_rate });
        setTrainers(prev => prev.map(t => t.id === selectedTrainer ? { ...t, default_mwst_rate: multipliers.mwst_rate } : t));
      }
      await loadProvision();
      setShowMultiplierDialog(false);
      setSaveMwstAsDefault(false);
    } catch (error) {
      console.error('Error saving multipliers:', error);
    }
    setSavingMultipliers(false);
  };

  const saveQuarterlyMultipliers = async () => {
    if (!selectedTrainer || !selectedQuarter) return;
    setSavingQuarterlyMult(true);
    try {
      await quarterlyProvisionsAPI.updateMultipliers(
        selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), quarterlyMultipliers
      );
      await loadQuarterlyProvision();
      setShowQuarterlyMultDialog(false);
    } catch (error) {
      console.error('Error saving quarterly multipliers:', error);
    }
    setSavingQuarterlyMult(false);
  };

  const shareCampaign = async () => {
    if (!shareTargetId || !selectedTrainer || !selectedCampaign) return;
    setSharing(true);
    try {
      const res = await campaignProvisionsAPI.share(selectedTrainer, selectedCampaign, shareTargetId);
      alert(res.message);
      setShowShareDialog(false);
      setShareTargetId('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Teilen der Kampagne');
    }
    setSharing(false);
  };

  useEffect(() => {
    if (selectedTrainer && selectedCampaign) {
      loadMultipliers();
    }
  }, [selectedTrainer, selectedCampaign]);

  // Add trainee in advance (with contact details, no units)
  const addTraineeInAdvance = async () => {
    if (!newTraineeData.name.trim()) return;
    setAddingTrainee(true);
    try {
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name: newTraineeData.name.trim(),
        email: newTraineeData.email.trim() || '',
        phone: newTraineeData.phone.trim() || '',
        address: newTraineeData.address.trim() || '',
        units_week1: 0,
        units_week2: 0,
        units_week3: 0,
        fee_received: false
      });
      await loadProvision();
      setNewTraineeData({ name: '', email: '', phone: '', address: '' });
      setShowAddTraineeDialog(false);
    } catch (error) {
      console.error('Error adding trainee:', error);
    }
    setAddingTrainee(false);
  };

  // Advance functions
  const addAdvance = async () => {
    if (!newAdvanceAmount) return;
    setSaving(true);
    try {
      await campaignProvisionsAPI.addAdvance(selectedTrainer, selectedCampaign, { amount: parseFloat(newAdvanceAmount), note: newAdvanceNote });
      setNewAdvanceAmount('');
      setNewAdvanceNote('');
      await loadProvision();
    } catch (error) {
      console.error('Error:', error);
    }
    setSaving(false);
  };

  const deleteAdvance = async (id) => {
    setSaving(true);
    try {
      await campaignProvisionsAPI.deleteAdvance(selectedTrainer, selectedCampaign, id);
      await loadProvision();
    } catch (error) {
      console.error('Error:', error);
    }
    setSaving(false);
  };

  // Payout functions
  const addPayout = async () => {
    if (!newPayoutAmount) return;
    setSaving(true);
    try {
      await campaignProvisionsAPI.addPayout(selectedTrainer, selectedCampaign, { amount: parseFloat(newPayoutAmount), note: newPayoutNote });
      setNewPayoutAmount('');
      setNewPayoutNote('');
      await loadProvision();
    } catch (error) {
      console.error('Error:', error);
    }
    setSaving(false);
  };

  const deletePayout = async (id) => {
    setSaving(true);
    try {
      await campaignProvisionsAPI.deletePayout(selectedTrainer, selectedCampaign, id);
      await loadProvision();
    } catch (error) {
      console.error('Error:', error);
    }
    setSaving(false);
  };

  // Profile functions
  const saveProfile = async () => {
    if (!selectedTrainer) return;
    setSavingProfile(true);
    try {
      await usersAPI.updateProfile(selectedTrainer, profileData);
      // Reload trainers to get updated profile
      const trainersData = await usersAPI.getAll();
      setTrainers(trainersData);
      setShowProfileEditor(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
    setSavingProfile(false);
  };

  // Invoice functions (Gutschrift)
  const downloadInvoice = async () => {
    if (!selectedTrainer || !selectedCampaign) return;
    setGeneratingInvoice(true);
    try {
      await invoiceAPI.download(selectedTrainer, selectedCampaign, selectedTrainerName);
    } catch (error) {
      console.error('Error generating credit note:', error);
      alert('Fehler beim Erstellen der Gutschrift. Bitte erneut versuchen – falls das Problem bleibt, prüfe ob für diese Auswahl Provisionsdaten vorhanden sind.');
    }
    setGeneratingInvoice(false);
  };

  // Create new trainer
  const createNewTrainer = async () => {
    if (!newTrainerData.name || !newTrainerData.email || !newTrainerData.password) {
      setTrainerError('Bitte alle Felder ausfüllen');
      return;
    }
    if (newTrainerData.password.length < 6) {
      setTrainerError('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    setCreatingTrainer(true);
    setTrainerError('');
    try {
      const result = await usersAPI.createTrainer({
        name: newTrainerData.name,
        email: newTrainerData.email,
        password: newTrainerData.password
      });
      // Reload trainers
      const trainersData = await usersAPI.getAll();
      setTrainers(trainersData);
      setShowNewTrainerDialog(false);
      setNewTrainerData({ name: '', email: '', password: '' });
      // Select the new trainer
      if (result.id) {
        setSelectedTrainer(result.id);
      }
    } catch (error) {
      setTrainerError(error.response?.data?.detail || 'Fehler beim Erstellen des Trainers');
    }
    setCreatingTrainer(false);
  };

  // Calculated values - Vereinfacht: Alle Trainees mit Einheiten (W1+W2 kombiniert)
  const trainees = provision?.trainees || [];
  // Trainees mit Kampagnen-Einheiten (W1 oder W2 > 0)
  const traineesWithUnits = trainees.filter(t => (t.units_week1 || 0) > 0 || (t.units_week2 || 0) > 0);
  const traineesWithCertificates = trainees.filter(t => t.certificate);
  // Gesamteinheiten = W1 + W2 (zusammengefasst) - W3/Neuwerber ist jetzt in Quartalsbereich
  const totalUnits = trainees.reduce((sum, t) => sum + (t.units_week1 || 0) + (t.units_week2 || 0), 0);
  const paidOutTrainees = trainees.filter(t => t.fee_received);
  const totalBruttoCombined = provision?.total_provision_brutto || 0;
  // total_payouts vom Backend enthält bereits die Summe der abgehakten Trainees
  const totalAusgezahlt = provision?.total_payouts || 0;
  const actualRestbetrag = provision?.remaining_amount || 0;

  // Legacy: Behalte für Abwärtskompatibilität
  const traineesW1 = trainees.filter(t => (t.units_week1 || 0) > 0);
  const traineesW2 = trainees.filter(t => (t.units_week2 || 0) > 0);
  const totalUnitsW1 = trainees.reduce((sum, t) => sum + (t.units_week1 || 0), 0);
  const totalUnitsW2 = trainees.reduce((sum, t) => sum + (t.units_week2 || 0), 0);

  const selectedTrainerName = trainers.find(t => t.id === selectedTrainer)?.name || '';
  const selectedCampaignName = campaigns.find(c => c.id === selectedCampaign)?.name || '';
  const selectedTrainerProfile = trainers.find(t => t.id === selectedTrainer)?.profile || {};
  const hasProfile = selectedTrainerProfile.company_name || selectedTrainerProfile.address;

  // Funktion zum Hinzufügen eines Trainees mit kombinierten Einheiten
  const addTraineeCombined = async (name, units) => {
    if (!name.trim() || !units) return;
    setSaving(true);
    try {
      // Speichere Einheiten in units_week1, Backend berechnet dann units = w1 + w2
      await campaignProvisionsAPI.addTrainee(selectedTrainer, selectedCampaign, {
        name: name.trim(),
        units_week1: parseFloat(units),
        units_week2: 0,
        units_week3: 0,
        fee_received: false
      });
      await loadProvision();
    } catch (error) {
      console.error('Error adding trainee:', error);
    }
    setSaving(false);
  };

  // Download all certificates as HTML page
  const downloadAllCertificates = () => {
    if (!traineesWithCertificates.length) {
      alert('Keine gespeicherten Zertifikate vorhanden. Erstellen und speichern Sie zuerst Zertifikate für Trainees.');
      return;
    }
    
    const campaign = campaigns.find(c => c.id === selectedCampaign);
    const today = new Date().toLocaleDateString('de-DE');
    
    const certificatesHtml = traineesWithCertificates.map(t => `
      <div class="certificate">
        <div class="watermark">
          <img src="${WAHR_LOGO_BASE64}" alt="" />
        </div>
        <div class="content-wrapper">
          <div class="header">
            <img src="${WAHR_LOGO_BASE64}" alt="WAHR Logo" class="logo" />
            <h2>SCHULUNGSZERTIFIKAT</h2>
            <h3>${t.name}</h3>
            <p class="topic">${t.certificate.topic || campaign?.name || ''}</p>
            <p class="date">${t.certificate.date_from || ''} – ${t.certificate.date_to || ''}</p>
          </div>
          <div class="body">
            <div class="section"><strong>Allgemeiner Eindruck:</strong><br>${t.certificate.allgemeiner_eindruck || ''}</div>
            <div class="section"><strong>Teamverhalten:</strong><br>${t.certificate.teamverhalten || ''}</div>
            <div class="section"><strong>Arbeitsweise:</strong><br>${t.certificate.arbeitsweise || ''}</div>
            <div class="section"><strong>Leistungsentwicklung:</strong><br>${t.certificate.leistungsentwicklung || ''}</div>
            <div class="section fazit"><strong>Fazit:</strong><br>${t.certificate.fazit || ''}</div>
          </div>
          <div class="footer">
            <div class="footer-left">
              <p class="label">Ausgestellt am</p>
              <p class="value">${today}</p>
            </div>
            <div class="footer-center">
              <img src="${SIGNATURE_BASE64}" alt="Unterschrift" class="signature" />
              <p class="signer">Nicolas Wahr</p>
              <p class="title">Geschäftsführer</p>
            </div>
            <div class="footer-right">
              <p class="label">Ort</p>
              <p class="value">Düsseldorf</p>
            </div>
          </div>
        </div>
      </div>
    `).join('<div class="page-break"></div>');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Zertifikate - ${campaign?.name || ''}</title>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; }
          .certificate { 
            background: white; 
            border: 2px solid #1c2e52; 
            border-radius: 12px; 
            padding: 30px; 
            margin-bottom: 30px; 
            max-width: 800px; 
            margin-left: auto; 
            margin-right: auto;
            position: relative;
            overflow: hidden;
          }
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            opacity: 0.06;
            pointer-events: none;
            z-index: 0;
          }
          .watermark img { width: 300px; height: auto; }
          .content-wrapper { position: relative; z-index: 1; }
          .header { text-align: center; border-bottom: 2px solid #e6d7bf; padding-bottom: 20px; margin-bottom: 20px; }
          .header .logo { width: 60px; height: auto; margin-bottom: 10px; }
          .header h2 { color: #1c2e52; margin: 0 0 10px 0; font-size: 24px; }
          .header h3 { color: #3e4350; margin: 0 0 5px 0; font-size: 20px; }
          .header .topic { color: #4b95d1; margin: 5px 0; font-weight: 500; }
          .header .date { color: #666; font-size: 14px; }
          .body { font-size: 14px; line-height: 1.6; }
          .section { margin-bottom: 15px; }
          .section strong { color: #1c2e52; }
          .fazit { border-top: 1px solid #e6d7bf; padding-top: 15px; font-style: italic; }
          .footer { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-end; 
            margin-top: 30px; 
            padding-top: 20px; 
            border-top: 1px solid #e6d7bf; 
          }
          .footer-left, .footer-right { text-align: left; }
          .footer-right { text-align: right; }
          .footer-center { text-align: center; }
          .footer .label { color: #666; font-size: 12px; margin: 0 0 4px 0; }
          .footer .value { color: #1c2e52; font-size: 14px; font-weight: 500; margin: 0; }
          .footer .signature { height: 50px; width: auto; margin-bottom: 5px; }
          .footer .signer { color: #333; font-size: 13px; margin: 0; border-top: 1px solid #ccc; padding-top: 5px; }
          .footer .title { color: #666; font-size: 11px; margin: 0; }
          .page-break { page-break-after: always; height: 1px; }
          .actions { text-align: center; margin: 20px 0; }
          .btn { display: inline-block; background: #4b95d1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 5px; cursor: pointer; border: none; font-size: 16px; }
          .btn:hover { background: #1c2e52; }
          .info { background: #1c2e52; color: white; padding: 15px; border-radius: 8px; text-align: center; margin-bottom: 20px; max-width: 800px; margin-left: auto; margin-right: auto; }
          @media print { 
            .actions, .info { display: none; } 
            .certificate { border: 1px solid #ccc; break-inside: avoid; }
            body { background: white; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="info">
          <strong>${traineesWithCertificates.length} Zertifikate</strong> für ${campaign?.name || ''}
        </div>
        <div class="actions">
          <button class="btn" onclick="window.print()">🖨️ Drucken / Als PDF speichern</button>
        </div>
        ${certificatesHtml}
      </body>
      </html>
    `;

    // Use Blob URL instead of document.write for security (XSS prevention)
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const newWindow = window.open(blobUrl, '_blank');
    if (!newWindow) {
      alert('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
    }
    // Clean up blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="w-[95vw] sm:w-full sm:max-w-6xl max-h-[90vh] flex flex-col p-3 sm:p-6"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-2 sm:pb-4">
          <DialogTitle className="flex items-center gap-2 text-[#1c2e52] text-sm sm:text-base">
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-[#4b95d1]" />
            <span className="hidden sm:inline">Provisionen verwalten</span>
            <span className="sm:hidden">Provisionen</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Selection Row - Stack on mobile, grid on desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="space-y-1">
                <Label className="text-[#3e4350] text-xs sm:text-sm">Trainer</Label>
                <div className="flex gap-1">
                  <Select value={selectedTrainer} onValueChange={setSelectedTrainer} disabled={readOnly}>
                    <SelectTrigger className="border-[#e6d7bf] flex-1 h-9 text-xs sm:text-sm">
                      <SelectValue placeholder="Trainer wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {trainers.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-[#4b95d1]" />
                            {t.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!readOnly && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewTrainerDialog(true)}
                    className="border-[#4b95d1] text-[#4b95d1] hover:bg-[#4b95d1] hover:text-white h-9 px-2"
                    title="Neuen Trainer anlegen"
                    data-testid="add-trainer-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-[#3e4350] text-xs sm:text-sm">Jahr</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="border-[#e6d7bf] h-9 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-[#3e4350] text-xs sm:text-sm">Kampagne</Label>
                <div className="flex gap-1">
                  <Select value={selectedCampaign} onValueChange={(value) => {
                    setSelectedCampaign(value);
                    setSelectedQuarter(''); // Reset quarter when campaign is selected
                  }}>
                    <SelectTrigger className={`h-9 text-xs sm:text-sm ${selectedCampaign ? 'border-[#4b95d1] bg-blue-50' : 'border-[#e6d7bf]'}`}>
                      <SelectValue placeholder="KW wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#4b95d1]" />
                            {c.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!readOnly && selectedCampaign && selectedTrainer && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 border-blue-300 text-[#4b95d1] hover:bg-blue-50 flex-shrink-0"
                      title="KWs ändern (Kampagne verschieben)"
                      data-testid="move-campaign-kw-btn"
                      onClick={() => {
                        const m = String(selectedCampaign).match(/kw-?(\d+)-(\d+)/i);
                        setMoveStartWeek(m ? m[1] : '');
                        setMoveEndWeek(m ? m[2] : '');
                        setShowMoveKwDialog(true);
                      }}
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                    </Button>
                  )}
                  {!readOnly && selectedCampaign && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 border-red-300 text-red-500 hover:bg-red-50 flex-shrink-0"
                      title="Kampagne löschen"
                      data-testid="delete-campaign-btn"
                      onClick={handleDeleteCampaign}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-[#3e4350] text-xs sm:text-sm flex items-center gap-1">
                  <CalendarDays className="w-3 h-3 text-purple-500" />
                  Quartal
                  {selectedQuarter && (
                    <Badge className="ml-1 bg-purple-500 text-white text-[10px] px-1.5 py-0">
                      Q{selectedQuarter}
                    </Badge>
                  )}
                </Label>
                <Select value={selectedQuarter} onValueChange={(value) => {
                  setSelectedQuarter(value);
                  setSelectedCampaign(''); // Reset campaign when quarter is selected
                }}>
                  <SelectTrigger className={`h-9 text-xs sm:text-sm ${selectedQuarter ? 'border-purple-500 bg-purple-100' : 'border-purple-300 bg-purple-50/50'}`} data-testid="quarter-select">
                    <SelectValue placeholder="Q wählen">
                      {selectedQuarter && (
                        <span className="font-medium text-purple-700">
                          Q{selectedQuarter} - {selectedQuarter === '1' ? 'Jan-Mär' : selectedQuarter === '2' ? 'Apr-Jun' : selectedQuarter === '3' ? 'Jul-Sep' : 'Okt-Dez'}
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                    <SelectItem value="1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-purple-600">Q1</span>
                        <span className="text-gray-500 text-xs">Jan-Mär</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-purple-600">Q2</span>
                        <span className="text-gray-500 text-xs">Apr-Jun</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-purple-600">Q3</span>
                        <span className="text-gray-500 text-xs">Jul-Sep</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-purple-600">Q4</span>
                        <span className="text-gray-500 text-xs">Okt-Dez</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedTrainer && selectedCampaign && provision && (
              <ScrollArea className="h-[calc(100vh-380px)] sm:h-[calc(100vh-320px)] pr-2 sm:pr-4">
                <div className="space-y-3 sm:space-y-4">
                  {/* Summary Card */}
                  <Card className="bg-gradient-to-r from-[#1c2e52] to-[#3e4350] text-white">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <h3 className="text-sm sm:text-lg font-semibold truncate">
                          {selectedTrainerName} - {selectedCampaignName}
                          {provision?.shared_from && (
                            <Badge className="ml-2 bg-white/20 text-white hover:bg-white/20 text-[10px] align-middle" data-testid="shared-campaign-badge">
                              <Users className="w-3 h-3 mr-1" />Geteilt
                            </Badge>
                          )}
                        </h3>
                        <div className="flex items-center gap-1 sm:gap-2">
                          {!readOnly && (<>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowMultiplierDialog(true)}
                            className="text-white hover:bg-white/20 h-8 w-8 p-0"
                            title="Multiplikatoren bearbeiten"
                          >
                            <Percent className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowShareDialog(true)}
                            className="text-white hover:bg-white/20 h-8 w-8 p-0"
                            title="Kampagne mit weiterem Trainer teilen"
                            data-testid="share-campaign-btn"
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowProfileEditor(true)}
                            className="text-white hover:bg-white/20"
                            title="Profil bearbeiten"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={downloadInvoice}
                            disabled={generatingInvoice || !provision.total_units}
                            className="text-white hover:bg-white/20"
                            title="Gutschrift erstellen"
                          >
                            {generatingInvoice ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                          </Button>
                          </>)}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={downloadAllCertificates}
                            className={`text-white hover:bg-white/20 ${traineesWithCertificates.length > 0 ? 'text-amber-300' : ''}`}
                            title={traineesWithCertificates.length > 0 ? `${traineesWithCertificates.length} Zertifikate herunterladen` : 'Keine Zertifikate gespeichert'}
                          >
                            <Award className="w-4 h-4" />
                            {traineesWithCertificates.length > 0 && (
                              <span className="ml-1 text-xs">{traineesWithCertificates.length}</span>
                            )}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={loadProvision} className="text-white hover:bg-white/20">
                            <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Profile warning */}
                      {!hasProfile && !readOnly && (
                        <div className="bg-amber-500/20 border border-amber-400/50 rounded p-2 mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <span className="text-xs text-amber-200">Profil unvollständig - ⚙️ klicken</span>
                        </div>
                      )}
                      
                      {/* Stats Grid - Mobile optimized - W3 now separate */}
                      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-1 sm:gap-2 text-xs sm:text-sm">
                        <div className="bg-blue-500/30 rounded p-1.5 sm:p-2">
                          <p className="text-blue-200 text-[10px] sm:text-xs">W1</p>
                          <p className="font-bold text-xs sm:text-sm">{formatNumber(totalUnitsW1)} E</p>
                        </div>
                        <div className="bg-indigo-500/30 rounded p-1.5 sm:p-2">
                          <p className="text-indigo-200 text-[10px] sm:text-xs">W2</p>
                          <p className="font-bold text-xs sm:text-sm">{formatNumber(totalUnitsW2)} E</p>
                        </div>
                        <div className="bg-white/10 rounded p-1.5 sm:p-2">
                          <p className="text-[#e6d7bf] text-[10px] sm:text-xs">Gesamt</p>
                          <p className="font-bold text-xs sm:text-sm">{formatNumber(provision.total_units || 0)} E</p>
                        </div>
                        <div className="bg-white/10 rounded p-1.5 sm:p-2 hidden sm:block">
                          <p className="text-[#e6d7bf] text-[10px] sm:text-xs">Netto</p>
                          <p className="font-bold text-xs sm:text-sm">{formatCurrency(provision.total_provision_netto)}</p>
                        </div>
                        <div className="bg-[#4b95d1]/30 rounded p-1.5 sm:p-2">
                          <p className="text-[#e6d7bf] text-[10px] sm:text-xs">Brutto</p>
                          <p className="font-bold text-xs sm:text-sm">{formatCurrency(provision.total_provision_brutto)}</p>
                        </div>
                        <div className="bg-amber-500/30 rounded p-1.5 sm:p-2 hidden md:block">
                          <p className="text-amber-200 text-[10px] sm:text-xs">Vorschüsse</p>
                          <p className="font-bold text-xs sm:text-sm">-{formatCurrency(provision.total_advances)}</p>
                        </div>
                        <div className="bg-green-500/30 rounded p-1.5 sm:p-2 hidden md:block">
                          <p className="text-green-200 text-[10px] sm:text-xs">Ausgezahlt</p>
                          <p className="font-bold text-xs sm:text-sm">-{formatCurrency(totalAusgezahlt)}</p>
                        </div>
                        <div className="bg-white/20 rounded p-1.5 sm:p-2 border border-white/30 col-span-2 sm:col-span-1">
                          <p className="text-[#e6d7bf] text-[10px] sm:text-xs">Restbetrag</p>
                          <p className="font-bold text-[#4b95d1] text-xs sm:text-sm">{formatCurrency(actualRestbetrag)}</p>
                        </div>
                      </div>
                      
                      {/* Hint about Quarterly Neuwerber */}
                      <div className="mt-3 bg-purple-500/20 border border-purple-400/40 rounded p-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-300" />
                        <span className="text-xs text-purple-200">
                          Neuwerber (W3) werden separat in der Quartalsabrechnung verwaltet - wähle ein Quartal oben aus
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Vorbereitung Section - Add Trainee in Advance */}
                  {(!readOnly || (provision?.trainees || []).some(t => (t.units_week1 || 0) === 0 && (t.units_week2 || 0) === 0 && (t.units_week3 || 0) === 0)) && (
                  <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 border-2 border-emerald-400 shadow-lg">
                    <CardHeader className="py-3 sm:py-4 px-4">
                      <CardTitle className="text-sm sm:text-base flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                        <div className="flex items-center gap-2 text-white">
                          <UserPlus className="w-5 h-5" />
                          <span className="font-bold">📋 Vorbereitung - Trainees vorab anlegen</span>
                        </div>
                        {!readOnly && (
                        <Button
                          size="sm"
                          onClick={() => setShowAddTraineeDialog(true)}
                          className="bg-white text-emerald-700 hover:bg-emerald-100 h-8 text-xs font-semibold"
                          data-testid="add-trainee-advance-btn"
                        >
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="hidden sm:inline">Trainee anlegen</span>
                          <span className="sm:hidden">Anlegen</span>
                        </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      {/* Show trainees without units */}
                      {(() => {
                        const traineesNoUnits = (provision?.trainees || []).filter(
                          t => (t.units_week1 || 0) === 0 && (t.units_week2 || 0) === 0 && (t.units_week3 || 0) === 0
                        );
                        if (traineesNoUnits.length === 0) {
                          return (
                            <div className="bg-white/20 rounded-lg p-4 text-center">
                              <UserPlus className="w-8 h-8 text-white/70 mx-auto mb-2" />
                              <p className="text-white text-sm font-medium">
                                Hier können Sie Trainees mit Kontaktdaten vorab anlegen
                              </p>
                              <p className="text-emerald-100 text-xs mt-1">
                                Bevor die Kampagne startet - inkl. E-Mail, Telefon & Adresse
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-3">
                            <p className="text-white font-medium text-sm">
                              {traineesNoUnits.length} Trainee(s) vorab angelegt:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {traineesNoUnits.map(t => (
                                <div key={t.id} className="bg-white rounded-lg p-3 flex items-center gap-2 shadow">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#1c2e52] text-sm truncate">{t.name}</p>
                                    {(t.email || t.phone) && (
                                      <p className="text-gray-500 text-xs truncate">
                                        {t.email}{t.email && t.phone ? ' | ' : ''}{t.phone}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => openCertificateDialog(t)}
                                    className={`h-7 w-7 p-0 inline-flex items-center justify-center rounded-full transition-colors ${
                                      t.certificate 
                                        ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                        : 'bg-gray-100 text-gray-500 hover:bg-[#1c2e52] hover:text-white'
                                    }`}
                                    title={t.certificate ? "Zertifikat anzeigen" : "Zertifikat erstellen"}
                                  >
                                    <Award className="w-4 h-4" />
                                  </button>
                                  {!readOnly && (<>
                                  <button
                                    onClick={() => openTraineeEditor(t)}
                                    className="h-7 w-7 p-0 inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-emerald-600 hover:text-white transition-colors"
                                    title="Bearbeiten"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteTrainee(t.id)}
                                    className="h-7 w-7 p-0 inline-flex items-center justify-center rounded-full bg-gray-100 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                                    title="Löschen"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  </>)}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                  )}

                  {/* Simplified: Combined Trainee Units Block (replaces Week 1 & 2) */}
                  <Card className="border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg">
                    <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-4">
                      <CardTitle className="text-sm sm:text-base flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-blue-700">
                          <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="font-bold text-sm sm:text-base">Kampagnen-Einheiten</span>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                            {formatNumber(totalUnits)} E
                          </Badge>
                        </div>
                        {!readOnly && (
                        <div className="flex items-center gap-1 sm:gap-2">
                          {/* OCR Upload Button */}
                          <input
                            type="file"
                            ref={fileInputRefW1}
                            onChange={handleFileSelectW1}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRefW1.current?.click()}
                            disabled={ocrLoadingW1}
                            className="border-blue-300 text-blue-600 hover:bg-blue-100 h-8 px-2 sm:px-3"
                          >
                            {ocrLoadingW1 ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                            <span className="ml-1 hidden sm:inline text-xs">Foto scannen</span>
                          </Button>
                        </div>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 sm:space-y-3 px-3 sm:px-4 pb-3 sm:pb-4">
                      {/* Add Trainee Form - Stack on mobile */}
                      {!readOnly && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          placeholder="Name"
                          value={newTraineeName}
                          onChange={(e) => setNewTraineeName(e.target.value)}
                          className="flex-1 border-blue-200 text-sm sm:text-base h-10"
                          data-testid="trainee-name-input"
                        />
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            placeholder="Einheiten"
                            value={newTraineeUnits}
                            onChange={(e) => setNewTraineeUnits(e.target.value)}
                            className="w-24 sm:w-28 border-blue-200 text-sm sm:text-base h-10"
                            data-testid="trainee-units-input"
                          />
                          <Button 
                            onClick={() => {
                              addTraineeCombined(newTraineeName, newTraineeUnits);
                              setNewTraineeName('');
                              setNewTraineeUnits('');
                            }}
                            className="bg-blue-500 hover:bg-blue-600 px-3 sm:px-4 h-10"
                            disabled={!newTraineeName.trim() || !newTraineeUnits || saving}
                            data-testid="add-trainee-btn"
                          >
                            <Plus className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                      )}

                      {/* OCR Results */}
                      {ocrResultW1 && (
                        <div className="bg-blue-100 border border-blue-300 rounded-lg p-2 sm:p-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                            <span className="text-xs sm:text-sm font-medium text-blue-700">
                              Erkannte Trainees ({ocrResultW1.trainees?.length || 0})
                            </span>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={addAllOcrTraineesW1} className="bg-blue-500 hover:bg-blue-600 text-xs h-7 px-2">
                                Alle hinzufügen
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setOcrResultW1(null)} className="text-xs h-7 px-2">
                                Schließen
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-1 max-h-[120px] sm:max-h-[150px] overflow-y-auto">
                            {ocrResultW1.trainees?.map((t, idx) => (
                              <div key={`ocr-${t.name}-${idx}`} className="flex items-center gap-2 bg-white rounded p-2">
                                <span className="flex-1 text-xs sm:text-sm truncate">{t.name}</span>
                                <Input
                                  type="number"
                                  value={t.units}
                                  onChange={(e) => setOcrResultW1(prev => ({ 
                                    ...prev, 
                                    trainees: prev.trainees.map((tr, i) => i === idx ? { ...tr, units: parseFloat(e.target.value) || 0 } : tr) 
                                  }))}
                                  className="w-14 sm:w-16 h-7 text-xs"
                                />
                                <Button size="sm" onClick={() => addOcrTraineeW1(t.name, t.units)} className="h-7 w-7 p-0 text-xs bg-green-500">
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Trainees List - All trainees with units */}
                      <div className="space-y-1 sm:space-y-2 max-h-[200px] sm:max-h-[300px] overflow-y-auto">
                        {traineesWithUnits.length === 0 ? (
                          <div className="text-center py-4 sm:py-6 text-blue-400">
                            <Users className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-xs sm:text-sm">Keine Trainees mit Einheiten</p>
                            <p className="text-[10px] sm:text-xs text-gray-400 mt-1">Fügen Sie Trainees hinzu</p>
                          </div>
                        ) : (
                          traineesWithUnits.map((trainee) => {
                            const traineeUnits = (trainee.units_week1 || 0) + (trainee.units_week2 || 0);
                            const traineeProvision = traineeUnits * (multipliers?.multiplier_w12 ?? 1.4) * (1 + (multipliers?.mwst_rate ?? 0.19));
                            return (
                              <div 
                                key={trainee.id}
                                className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border-2 transition-all ${
                                  trainee.fee_received 
                                    ? 'bg-green-50 border-green-300' 
                                    : 'bg-white border-gray-200 hover:border-blue-300'
                                }`}
                              >
                                <Checkbox
                                  checked={trainee.fee_received}
                                  disabled={readOnly}
                                  onCheckedChange={(checked) => updateTraineeFeeStatus(trainee.id, checked)}
                                  className="h-4 w-4 sm:h-5 sm:w-5 border-2 border-green-500 data-[state=checked]:bg-green-500 flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className={`text-xs sm:text-sm font-medium block truncate ${trainee.fee_received ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                    {trainee.name}
                                  </span>
                                </div>
                                <Badge className="bg-blue-100 text-blue-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 flex-shrink-0">
                                  {formatNumber(traineeUnits)} E
                                </Badge>
                                <div className="text-right flex-shrink-0 min-w-[50px] sm:min-w-[80px]">
                                  <span className="font-bold text-blue-600 text-xs sm:text-sm block">
                                    {formatCurrency(traineeProvision)}
                                  </span>
                                  <span className="text-[9px] sm:text-[10px] text-emerald-600 block" data-testid={`institut-per-trainee-${trainee.id}`}>
                                    Institut: {formatCurrency(traineeUnits * 2 * 1.19)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => openCertificateDialog(trainee)}
                                    className={`h-7 w-7 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-lg transition-colors ${
                                      trainee.certificate 
                                        ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                        : 'bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600'
                                    }`}
                                    title={trainee.certificate ? "Zertifikat" : "Zertifikat erstellen"}
                                    data-testid={`trainee-cert-btn-${trainee.id}`}
                                  >
                                    <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </button>
                                  {!readOnly && (<>
                                  <button
                                    onClick={() => openTraineeEditor(trainee)}
                                    className="h-7 w-7 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                                    title="Bearbeiten"
                                  >
                                    <Edit3 className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteTrainee(trainee.id)}
                                    className="h-7 w-7 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-lg bg-gray-100 text-red-500 hover:bg-red-100 transition-colors"
                                    title="Löschen"
                                  >
                                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </button>
                                  </>)}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Summary */}
                      {traineesWithUnits.length > 0 && (
                        <div className="flex items-center justify-between bg-blue-100 rounded-lg p-2 sm:p-3 mt-2">
                          <span className="text-xs sm:text-sm font-medium text-blue-700">
                            {traineesWithUnits.length} Trainee(s) · {paidOutTrainees.length} bezahlt
                          </span>
                          <span className="font-bold text-blue-700 text-sm sm:text-base">
                            {formatCurrency(provision?.total_provision_brutto || 0)}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Advances and Payouts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Advances */}
                    <Card className="border-amber-200 bg-amber-50/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                          <ArrowUpCircle className="w-4 h-4" />
                          Vorschüsse ({provision.advances?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {!readOnly && (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Euro className="absolute left-2 top-2.5 h-4 w-4 text-amber-500" />
                            <Input type="number" step="0.01" placeholder="Betrag" value={newAdvanceAmount} onChange={(e) => setNewAdvanceAmount(e.target.value)} className="pl-8 border-amber-200" />
                          </div>
                          <Input placeholder="Notiz" value={newAdvanceNote} onChange={(e) => setNewAdvanceNote(e.target.value)} className="flex-1 border-amber-200" />
                          <Button onClick={addAdvance} size="sm" className="bg-amber-500 hover:bg-amber-600" disabled={!newAdvanceAmount || saving}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        )}
                        <div className="space-y-1 max-h-[100px] overflow-y-auto">
                          {(provision.advances || []).map((adv) => (
                            <div key={adv.id} className="flex items-center gap-2 p-2 rounded bg-white border border-amber-200">
                              <span className="text-sm font-medium text-amber-700">{formatCurrency(adv.amount)}</span>
                              {adv.note && <span className="flex-1 text-xs text-gray-500 truncate">{adv.note}</span>}
                              {!readOnly && (
                              <Button variant="ghost" size="sm" onClick={() => deleteAdvance(adv.id)} className="h-6 w-6 p-0 text-red-500">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Payouts */}
                    <Card className="border-green-200 bg-green-50/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-green-700">
                          <ArrowDownCircle className="w-4 h-4" />
                          Auszahlungen ({provision.payouts?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {!readOnly && (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Euro className="absolute left-2 top-2.5 h-4 w-4 text-green-500" />
                            <Input type="number" step="0.01" placeholder="Betrag" value={newPayoutAmount} onChange={(e) => setNewPayoutAmount(e.target.value)} className="pl-8 border-green-200" />
                          </div>
                          <Input placeholder="Notiz" value={newPayoutNote} onChange={(e) => setNewPayoutNote(e.target.value)} className="flex-1 border-green-200" />
                          <Button onClick={addPayout} size="sm" className="bg-green-500 hover:bg-green-600" disabled={!newPayoutAmount || saving}>
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        )}
                        <div className="space-y-1 max-h-[100px] overflow-y-auto">
                          {(provision.payouts || []).map((pay) => (
                            <div key={pay.id} className="flex items-center gap-2 p-2 rounded bg-white border border-green-200">
                              <span className="text-sm font-medium text-green-700">{formatCurrency(pay.amount)}</span>
                              {pay.note && <span className="flex-1 text-xs text-gray-500 truncate">{pay.note}</span>}
                              {!readOnly && (
                              <Button variant="ghost" size="sm" onClick={() => deletePayout(pay.id)} className="h-6 w-6 p-0 text-red-500">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Paid out trainees */}
                        {paidOutTrainees.length > 0 && (
                          <div className="pt-2 border-t border-green-200">
                            <p className="text-xs text-green-700 font-medium mb-2 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Ausgezahlte Trainees ({paidOutTrainees.length})
                            </p>
                            <div className="space-y-1 max-h-[80px] overflow-y-auto">
                              {paidOutTrainees.map((t) => (
                                <div key={t.id} className="flex items-center gap-2 p-2 rounded bg-green-100/50 border border-green-200">
                                  <Checkbox checked={t.fee_received} disabled={readOnly} onCheckedChange={(c) => updateTraineeFeeStatus(t.id, c)} className="border-green-500 data-[state=checked]:bg-green-500" />
                                  <span className="flex-1 text-sm text-green-800 truncate">{t.name}</span>
                                  <Badge variant="outline" className="text-xs border-green-300 text-green-700">{formatNumber(t.units)} E</Badge>
                                  <span className="text-sm font-medium text-green-700">{formatCurrency(t.provision_brutto)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                </div>
              </ScrollArea>
            )}

            {/* Quarterly Neuwerber Section - Shows when Quarter is selected */}
            {selectedTrainer && selectedQuarter && quarterlyProvision && (
              <div className="mt-4 pb-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 pt-3 sm:pt-4 border-t-4 border-purple-400">
                  <div className="bg-purple-500 text-white p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm sm:text-lg font-bold text-purple-700 truncate">
                      Q{selectedQuarter}/{selectedYear} - Neuwerber
                    </h2>
                    <p className="text-xs sm:text-sm text-purple-500 truncate">
                      {selectedTrainerName}
                    </p>
                  </div>
                </div>

                <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm sm:text-base flex flex-col sm:flex-row sm:items-center justify-between text-purple-700 gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">Neuwerber ({quarterlyProvision.trainees?.length || 0}, {formatNumber(quarterlyProvision.total_units || 0)} E)</span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] sm:text-xs border-purple-400 text-purple-600 bg-purple-100 ${readOnly ? '' : 'cursor-pointer hover:bg-purple-200'}`}
                          onClick={() => { if (!readOnly) setShowQuarterlyMultDialog(true); }}
                          title="Quartals-Faktoren bearbeiten"
                          data-testid="quarterly-mult-badge"
                        >
                          <Percent className="w-3 h-3 mr-0.5" />
                          ×{quarterlyMultipliers.multiplier} · USt {Math.round((quarterlyMultipliers.mwst_rate || 0) * 100)}%
                        </Badge>
                        <Badge variant="outline" className="text-[10px] sm:text-xs border-purple-400 text-purple-600 bg-white">
                          Netto: {formatCurrency(quarterlyProvision.total_provision_netto || 0)}
                        </Badge>
                        <Badge className="text-[10px] sm:text-xs bg-purple-600 text-white">
                          Brutto: {formatCurrency(quarterlyProvision.total_provision_brutto || 0)}
                        </Badge>
                        {(quarterlyProvision.remaining_amount ?? 0) > 0 && (
                          <Badge className="text-[10px] sm:text-xs bg-amber-500 text-white">
                            Offen: {formatCurrency(quarterlyProvision.remaining_amount)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        {!readOnly && (<>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowQuarterlyMultDialog(true)}
                          className="text-xs border-purple-400 text-purple-600 hover:bg-purple-100"
                          data-testid="quarterly-faktoren-btn"
                          title="Quartals-Faktoren & USt bearbeiten"
                        >
                          <Percent className="w-3 h-3 mr-1" />
                          Faktoren
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRefW3}
                          onChange={async (event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                              setOcrErrorW3('Nur JPEG, PNG oder WEBP erlaubt');
                              return;
                            }
                            setOcrLoadingW3(true);
                            setOcrErrorW3(null);
                            try {
                              const result = await ocrAPI.extractTrainees(file);
                              if (result.trainees) {
                                result.trainees = result.trainees.map(t => ({ ...t, units: t.units_week1 || t.units || 0 }));
                              }
                              setOcrResultW3(result);
                            } catch (error) {
                              setOcrErrorW3(error.response?.data?.detail || 'Fehler');
                            }
                            setOcrLoadingW3(false);
                            if (fileInputRefW3.current) fileInputRefW3.current.value = '';
                          }}
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          data-testid="ocr-file-input-quarterly"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRefW3.current?.click()}
                          disabled={ocrLoadingW3}
                          className="text-xs border-purple-400 text-purple-600 hover:bg-purple-100"
                          data-testid="ocr-upload-btn-quarterly"
                        >
                          {ocrLoadingW3 ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Camera className="w-3 h-3 mr-1" />
                          )}
                          Foto scannen
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={async () => {
                            try {
                              await quarterlyProvisionsAPI.downloadReport(
                                selectedTrainer, 
                                parseInt(selectedYear), 
                                parseInt(selectedQuarter), 
                                selectedTrainerName
                              );
                            } catch (error) {
                              console.error('Error downloading report:', error);
                              alert('Fehler beim Erstellen des Quartalsberichts');
                            }
                          }}
                          disabled={!quarterlyProvision?.trainees?.length}
                          className="text-xs border-purple-400 text-purple-600 hover:bg-purple-100"
                          data-testid="download-quarterly-report"
                          title="Quartalsbericht als PDF herunterladen"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Bericht
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={async () => {
                            try {
                              await quarterlyProvisionsAPI.downloadInvoice(
                                selectedTrainer, 
                                parseInt(selectedYear), 
                                parseInt(selectedQuarter), 
                                selectedTrainerName
                              );
                            } catch (error) {
                              console.error('Error downloading invoice:', error);
                              alert('Fehler beim Erstellen der Gutschrift. Bitte erneut versuchen – falls das Problem bleibt, prüfe ob für diese Auswahl Provisionsdaten vorhanden sind.');
                            }
                          }}
                          disabled={!quarterlyProvision?.trainees?.length}
                          className="text-xs border-green-500 text-green-600 hover:bg-green-100"
                          data-testid="download-quarterly-invoice"
                          title="Gutschrift als PDF herunterladen"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Gutschrift
                        </Button>
                        </>)}
                        <Button variant="ghost" size="sm" onClick={loadQuarterlyProvision} className="text-purple-600 hover:bg-purple-100">
                          <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* OCR Results for Quarterly */}
                    {ocrResultW3 && ocrResultW3.trainees && ocrResultW3.trainees.length > 0 && (
                      <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-purple-700">
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm font-medium">{ocrResultW3.trainees.length} erkannt</span>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              onClick={async () => {
                                if (!ocrResultW3?.trainees?.length) return;
                                setSaving(true);
                                for (const t of ocrResultW3.trainees) {
                                  await quarterlyProvisionsAPI.addTrainee(selectedTrainer, parseInt(selectedYear), parseInt(selectedQuarter), {
                                    name: t.name, units: t.units, fee_received: false
                                  });
                                }
                                await loadQuarterlyProvision();
                                setOcrResultW3(null);
                                setSaving(false);
                              }} 
                              disabled={saving} 
                              className="text-xs bg-purple-600 hover:bg-purple-700"
                            >
                              Alle hinzufügen
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setOcrResultW3(null)} className="text-xs text-purple-600">×</Button>
                          </div>
                        </div>
                        <div className="space-y-1 max-h-[100px] overflow-y-auto">
                          {ocrResultW3.trainees.map((trainee, idx) => (
                            <div key={`ocr-w3-${trainee.name}-${idx}`} className="flex items-center gap-2 p-2 bg-white rounded border border-purple-200">
                              <span className="flex-1 text-sm truncate">{trainee.name}</span>
                              <Input
                                type="number"
                                step="0.01"
                                value={trainee.units}
                                onChange={(e) => setOcrResultW3(prev => ({ ...prev, trainees: prev.trainees.map((t, i) => i === idx ? { ...t, units: parseFloat(e.target.value) || 0 } : t) }))}
                                className="w-20 h-7 text-xs"
                              />
                              <span className="text-xs text-gray-500">E</span>
                              <Button 
                                size="sm" 
                                onClick={async () => {
                                  await addQuarterlyTrainee(trainee.name, trainee.units);
                                  setOcrResultW3(prev => ({ ...prev, trainees: prev.trainees.filter(t => t.name !== trainee.name) }));
                                }} 
                                disabled={saving} 
                                className="h-7 text-xs bg-purple-500 hover:bg-purple-600"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {ocrErrorW3 && (
                      <div className="bg-red-50 border border-red-200 rounded p-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          <span className="text-sm text-red-700">{ocrErrorW3}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setOcrErrorW3(null)} className="text-red-500 h-6 px-2">×</Button>
                      </div>
                    )}

                    {/* Add new quarterly trainee */}
                    {!readOnly && (
                    <div className="flex gap-1 sm:gap-2">
                      <Input
                        placeholder="Name"
                        id="newQuarterlyTraineeName"
                        className="flex-1 border-purple-300 focus:border-purple-500 text-sm h-8 sm:h-9"
                        data-testid="quarterly-trainee-name"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const nameInput = document.getElementById('newQuarterlyTraineeName');
                            const unitsInput = document.getElementById('newQuarterlyTraineeUnits');
                            if (nameInput.value && unitsInput.value) {
                              addQuarterlyTrainee(nameInput.value.trim(), parseFloat(unitsInput.value));
                              nameInput.value = '';
                              unitsInput.value = '';
                            }
                          }
                        }}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="E"
                        id="newQuarterlyTraineeUnits"
                        className="w-16 sm:w-24 border-purple-300 focus:border-purple-500 text-sm h-8 sm:h-9"
                        data-testid="quarterly-trainee-units"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const nameInput = document.getElementById('newQuarterlyTraineeName');
                            const unitsInput = document.getElementById('newQuarterlyTraineeUnits');
                            if (nameInput.value && unitsInput.value) {
                              addQuarterlyTrainee(nameInput.value.trim(), parseFloat(unitsInput.value));
                              nameInput.value = '';
                              unitsInput.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 h-8 sm:h-9 px-2 sm:px-3"
                        disabled={saving}
                        data-testid="add-quarterly-trainee"
                        onClick={() => {
                          const nameInput = document.getElementById('newQuarterlyTraineeName');
                          const unitsInput = document.getElementById('newQuarterlyTraineeUnits');
                          if (nameInput.value && unitsInput.value) {
                            addQuarterlyTrainee(nameInput.value.trim(), parseFloat(unitsInput.value));
                            nameInput.value = '';
                            unitsInput.value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    )}

                    {/* Quarterly trainees list with W3 status */}
                    <div className="space-y-1 max-h-[150px] sm:max-h-[250px] overflow-y-auto">
                      {(!quarterlyProvision.trainees || quarterlyProvision.trainees.length === 0) ? (
                        <div className="text-center py-6 text-purple-400">
                          <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Keine Neuwerber für Q{selectedQuarter}/{selectedYear}</p>
                        </div>
                      ) : (
                        quarterlyProvision.trainees.map((trainee) => {
                          const hasW3 = (trainee.units || 0) > 0;
                          return (
                            <div key={trainee.id} className={`flex items-center gap-2 p-2 rounded border ${
                              trainee.fee_received 
                                ? 'bg-green-50 border-green-300' 
                                : hasW3 
                                  ? 'bg-white border-purple-200' 
                                  : 'bg-gray-50 border-gray-200'
                            }`}>
                              <Checkbox
                                checked={trainee.fee_received}
                                disabled={readOnly}
                                onCheckedChange={(checked) => updateQuarterlyTraineeFeeStatus(trainee.id, checked)}
                                className="border-purple-400 data-[state=checked]:bg-purple-500"
                              />
                              {/* W3 Status Badge */}
                              <div className="flex-shrink-0">
                                {hasW3 ? (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600" title="Hat W3 gemacht">
                                    <CheckCircle className="w-3 h-3" />
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-400" title="Noch keine W3">
                                    <Circle className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                              <span className={`flex-1 text-sm truncate ${
                                trainee.fee_received 
                                  ? 'line-through text-gray-400' 
                                  : hasW3 
                                    ? '' 
                                    : 'text-gray-500'
                              }`}>
                                {trainee.name}
                              </span>
                              {trainee.from_campaign && (
                                <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500 px-1">
                                  aus Kampagne
                                </Badge>
                              )}
                              <Badge variant="outline" className={`text-xs ${hasW3 ? 'border-purple-300' : 'border-gray-300 text-gray-500'}`}>
                                {formatNumber(trainee.units || 0)} E
                              </Badge>
                              <span className={`text-sm font-medium ${hasW3 ? 'text-purple-700' : 'text-gray-400'}`}>
                                {formatCurrency(trainee.provision_brutto || (trainee.units * quarterlyMultipliers.multiplier * (1 + quarterlyMultipliers.mwst_rate)))}
                              </span>
                              <button
                                onClick={() => {
                                  setCertificateTrainee({...trainee, isQuarterly: true});
                                  setShowCertificateDialog(true);
                                }}
                                className={`h-6 w-6 p-0 inline-flex items-center justify-center rounded transition-colors ${
                                  trainee.certificate 
                                    ? 'text-amber-500 bg-amber-50 hover:bg-amber-100' 
                                    : 'text-gray-400 hover:bg-gray-100'
                                }`}
                                title={trainee.certificate ? "Zertifikat anzeigen" : "Zertifikat erstellen"}
                              >
                                <Award className="w-3 h-3" />
                              </button>
                              {!readOnly && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteQuarterlyTrainee(trainee.id)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Quarterly Summary with W3 Status Statistics */}
                    {quarterlyProvision.trainees && quarterlyProvision.trainees.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-200 space-y-3">
                        {/* W3 Status Overview */}
                        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-700">
                                {quarterlyProvision.trainees.filter(t => (t.units || 0) > 0).length} mit W3
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Circle className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-500">
                                {quarterlyProvision.trainees.filter(t => (t.units || 0) === 0).length} noch keine W3
                              </span>
                            </div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {quarterlyProvision.trainees.length} Gesamt
                          </span>
                        </div>
                        
                        {/* Total Provision */}
                        <div className="bg-purple-100 rounded-lg p-3 space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-purple-700">Summe Neuwerber Q{selectedQuarter}:</span>
                            <span className="text-lg font-bold text-purple-700">{formatCurrency(quarterlyProvision.total_provision_brutto || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs text-purple-600 flex-wrap gap-1">
                            <span>Netto: {formatCurrency(quarterlyProvision.total_provision_netto || 0)}</span>
                            <span>Ausgezahlt: -{formatCurrency(quarterlyProvision.total_payouts || 0)} · Offen: {formatCurrency(quarterlyProvision.remaining_amount ?? (quarterlyProvision.total_provision_brutto || 0))}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {(!selectedTrainer || (!selectedCampaign && !selectedQuarter)) && (
              <div className="flex items-center justify-center h-64 text-[#3e4350]">
                <div className="text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-[#e6d7bf]" />
                  <p>Bitte wählen Sie einen Trainer und eine Kampagne oder ein Quartal aus.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
      
    {/* Profile Editor Dialog - Outside main dialog to fix z-index */}
    <Dialog open={showProfileEditor} onOpenChange={setShowProfileEditor}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <Building className="w-5 h-5 text-[#4b95d1]" />
              Trainer-Profil bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input 
                value={profileData.name || ''} 
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                placeholder="Name des Trainers"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Firma</Label>
              <Input 
                value={profileData.company_name || ''} 
                onChange={(e) => setProfileData({...profileData, company_name: e.target.value})}
                placeholder="Firmenname (optional)"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input 
                value={profileData.address || ''} 
                onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                placeholder="Straße und Hausnummer"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PLZ</Label>
                <Input 
                  value={profileData.zip_code || ''} 
                  onChange={(e) => setProfileData({...profileData, zip_code: e.target.value})}
                  placeholder="PLZ"
                />
              </div>
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input 
                  value={profileData.city || ''} 
                  onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                  placeholder="Stadt"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Steuernummer / USt-ID</Label>
              <Input 
                value={profileData.tax_number || ''} 
                onChange={(e) => setProfileData({...profileData, tax_number: e.target.value})}
                placeholder="z.B. 123/456/78901"
              />
            </div>
            
            <div className="pt-2 border-t">
              <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Bankverbindung
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input 
                    value={profileData.iban || ''} 
                    onChange={(e) => setProfileData({...profileData, iban: e.target.value})}
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC</Label>
                  <Input 
                    value={profileData.bic || ''} 
                    onChange={(e) => setProfileData({...profileData, bic: e.target.value})}
                    placeholder="COBADEFFXXX"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowProfileEditor(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={saveProfile} 
                disabled={savingProfile}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                {savingProfile ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* New Trainer Dialog - Outside main dialog to fix z-index */}
      <Dialog open={showNewTrainerDialog} onOpenChange={setShowNewTrainerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <User className="w-5 h-5 text-[#4b95d1]" />
              Neuen Trainer anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {trainerError && (
              <div className="bg-red-50 border border-red-200 rounded p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700">{trainerError}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={newTrainerData.name} 
                onChange={(e) => setNewTrainerData({...newTrainerData, name: e.target.value})}
                placeholder="Vor- und Nachname"
                data-testid="new-trainer-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>E-Mail *</Label>
              <Input 
                type="email"
                value={newTrainerData.email} 
                onChange={(e) => setNewTrainerData({...newTrainerData, email: e.target.value})}
                placeholder="email@beispiel.de"
                data-testid="new-trainer-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Passwort *</Label>
              <Input 
                type="password"
                value={newTrainerData.password} 
                onChange={(e) => setNewTrainerData({...newTrainerData, password: e.target.value})}
                placeholder="Mindestens 6 Zeichen"
                data-testid="new-trainer-password"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowNewTrainerDialog(false);
                setNewTrainerData({ name: '', email: '', password: '' });
                setTrainerError('');
              }}>
                Abbrechen
              </Button>
              <Button 
                onClick={createNewTrainer} 
                disabled={creatingTrainer}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                data-testid="create-trainer-btn"
              >
                {creatingTrainer ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Trainer anlegen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Kampagne teilen Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <Users className="w-5 h-5 text-[#4b95d1]" />
              Kampagne mit Trainer teilen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Kopiert alle Trainees + Einheiten von <b>{selectedTrainerName}</b> ({selectedCampaignName}) zum
              gewählten Trainer. Dessen Provision wird dort mit <b>eigenen Faktoren/USt</b> berechnet
              (über den %-Dialog einstellbar). Erneutes Teilen synchronisiert die Trainees.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Ziel-Trainer</Label>
              <Select value={shareTargetId} onValueChange={setShareTargetId}>
                <SelectTrigger data-testid="share-target-select">
                  <SelectValue placeholder="Trainer wählen" />
                </SelectTrigger>
                <SelectContent>
                  {trainers.filter(t => t.id !== selectedTrainer).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 text-xs text-blue-700">
              Hinweis: Der UG-Anteil zählt die Einheiten nur einmal (UG-Faktor der Kopie = 0, anpassbar).
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowShareDialog(false)}>Abbrechen</Button>
              <Button onClick={shareCampaign} disabled={sharing || !shareTargetId}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]" data-testid="share-confirm-btn">
                {sharing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />}
                Teilen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* KW ändern Dialog */}
      <Dialog open={showQuarterlyMultDialog} onOpenChange={setShowQuarterlyMultDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Percent className="w-5 h-5" />
              Quartals-Faktoren (Woche 3 / Neuwerber)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Multiplikator (Std: 1,0)</Label>
                <Input type="number" step="0.1" min="0" value={quarterlyMultipliers.multiplier}
                  data-testid="quarterly-multiplier-input"
                  onChange={(e) => setQuarterlyMultipliers({...quarterlyMultipliers, multiplier: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">USt-Satz (Std: 0,19)</Label>
                <Input type="number" step="0.01" min="0" max="1" value={quarterlyMultipliers.mwst_rate}
                  data-testid="quarterly-mwst-input"
                  onChange={(e) => setQuarterlyMultipliers({...quarterlyMultipliers, mwst_rate: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">UG-Faktor € (Std: 2,00)</Label>
                <Input type="number" step="0.1" min="0" value={quarterlyMultipliers.ug_factor}
                  data-testid="quarterly-ug-factor-input"
                  onChange={(e) => setQuarterlyMultipliers({...quarterlyMultipliers, ug_factor: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">UG-USt (Std: 0,19)</Label>
                <Input type="number" step="0.01" min="0" max="1" value={quarterlyMultipliers.ug_mwst_rate}
                  data-testid="quarterly-ug-mwst-input"
                  onChange={(e) => setQuarterlyMultipliers({...quarterlyMultipliers, ug_mwst_rate: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 text-xs text-purple-700">
              Neuwerber: Einheiten × {quarterlyMultipliers.multiplier} × {(1 + quarterlyMultipliers.mwst_rate).toFixed(2)} · UG: Einheiten × {quarterlyMultipliers.ug_factor} € × {(1 + quarterlyMultipliers.ug_mwst_rate).toFixed(2)} − Provision
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowQuarterlyMultDialog(false)}>Abbrechen</Button>
              <Button onClick={saveQuarterlyMultipliers} disabled={savingQuarterlyMult}
                className="bg-purple-600 hover:bg-purple-700" data-testid="quarterly-mult-save-btn">
                {savingQuarterlyMult ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showMoveKwDialog} onOpenChange={setShowMoveKwDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <ArrowRightLeft className="w-5 h-5 text-[#4b95d1]" />
              KWs ändern
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Verschiebt die Kampagne <b>{campaigns.find(c => c.id === selectedCampaign)?.name || selectedCampaign}</b> von{' '}
              <b>{trainers.find(t => t.id === selectedTrainer)?.name || 'diesem Trainer'}</b> in neue Kalenderwochen ({selectedYear}).
              Der Zeitstrahl wird automatisch angepasst.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Start-KW</Label>
                <Input
                  type="number" min="1" max="52" value={moveStartWeek}
                  data-testid="move-kw-start-input"
                  onChange={(e) => {
                    setMoveStartWeek(e.target.value);
                    const v = parseInt(e.target.value);
                    if (!isNaN(v)) setMoveEndWeek(String(v + 1));
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>End-KW</Label>
                <Input
                  type="number" min="2" max="53" value={moveEndWeek}
                  data-testid="move-kw-end-input"
                  onChange={(e) => setMoveEndWeek(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowMoveKwDialog(false)}>Abbrechen</Button>
              <Button onClick={handleMoveKw} disabled={movingKw} className="bg-[#1c2e52]" data-testid="move-kw-confirm-btn">
                {movingKw ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                Verschieben
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Certificate Dialog - Already outside via custom implementation */}
      <CertificateDialog
        open={showCertificateDialog}
        onClose={() => setShowCertificateDialog(false)}
        traineeName={certificateTrainee?.name || ''}
        traineeId={certificateTrainee?.id}
        traineeEmail={certificateTrainee?.email}
        savedCertificate={certificateTrainee?.certificate}
        onSaveCertificate={saveCertificateForTrainee}
        campaign={campaigns.find(c => c.id === selectedCampaign) || (selectedCampaign ? { id: selectedCampaign } : null)}
      />
      
      {/* Trainee Editor Dialog */}
      <Dialog open={showTraineeEditor} onOpenChange={setShowTraineeEditor}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <User className="w-5 h-5 text-[#4b95d1]" />
              Trainee bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          {editingTrainee && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={editingTrainee.name || ''} 
                  onChange={(e) => setEditingTrainee({...editingTrainee, name: e.target.value})}
                  placeholder="Vor- und Nachname"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  E-Mail
                </Label>
                <Input 
                  type="email"
                  value={editingTrainee.email || ''} 
                  onChange={(e) => setEditingTrainee({...editingTrainee, email: e.target.value})}
                  placeholder="email@beispiel.de"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  Telefon
                </Label>
                <Input 
                  type="tel"
                  value={editingTrainee.phone || ''} 
                  onChange={(e) => setEditingTrainee({...editingTrainee, phone: e.target.value})}
                  placeholder="+49 123 456789"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Adresse
                </Label>
                <Textarea 
                  value={editingTrainee.address || ''} 
                  onChange={(e) => setEditingTrainee({...editingTrainee, address: e.target.value})}
                  placeholder="Straße, PLZ, Ort"
                  className="min-h-[80px]"
                />
              </div>
              
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p className="text-gray-500">Einheiten:</p>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <span className="text-xs text-gray-400">Woche 1</span>
                    <p className="font-medium">{formatNumber(editingTrainee.units_week1 || 0)} E</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Woche 2</span>
                    <p className="font-medium">{formatNumber(editingTrainee.units_week2 || 0)} E</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowTraineeEditor(false); setEditingTrainee(null); }}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={saveTraineeDetails} 
                  disabled={savingTrainee}
                  className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                >
                  {savingTrainee ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Trainee in Advance Dialog */}
      <Dialog open={showAddTraineeDialog} onOpenChange={setShowAddTraineeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Trainee vorab anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Legen Sie einen Trainee mit Kontaktdaten an. Einheiten können später in den Wochen-Blöcken hinzugefügt werden.
            </p>
            
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input 
                value={newTraineeData.name} 
                onChange={(e) => setNewTraineeData({...newTraineeData, name: e.target.value})}
                placeholder="Vor- und Nachname"
                data-testid="new-trainee-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                E-Mail
              </Label>
              <Input 
                type="email"
                value={newTraineeData.email} 
                onChange={(e) => setNewTraineeData({...newTraineeData, email: e.target.value})}
                placeholder="email@beispiel.de"
                data-testid="new-trainee-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                Telefon
              </Label>
              <Input 
                type="tel"
                value={newTraineeData.phone} 
                onChange={(e) => setNewTraineeData({...newTraineeData, phone: e.target.value})}
                placeholder="+49 123 456789"
                data-testid="new-trainee-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                Adresse
              </Label>
              <Textarea 
                value={newTraineeData.address} 
                onChange={(e) => setNewTraineeData({...newTraineeData, address: e.target.value})}
                placeholder="Straße, PLZ, Ort"
                className="min-h-[60px]"
                data-testid="new-trainee-address"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowAddTraineeDialog(false);
                setNewTraineeData({ name: '', email: '', phone: '', address: '' });
              }}>
                Abbrechen
              </Button>
              <Button 
                onClick={addTraineeInAdvance} 
                disabled={addingTrainee || !newTraineeData.name.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-new-trainee-btn"
              >
                {addingTrainee ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Trainee anlegen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Multiplier Settings Dialog */}
      <Dialog open={showMultiplierDialog} onOpenChange={setShowMultiplierDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
              <Percent className="w-5 h-5 text-[#4b95d1]" />
              Multiplikatoren bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Passen Sie die Berechnungsfaktoren für diesen Trainer und diese Kampagne an.
            </p>
            
            <div className="space-y-4 bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Woche 1+2 Multiplikator</span>
                  <span className="text-xs text-gray-400">Standard: 1,4</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    step="0.1"
                    min="0"
                    value={multipliers.multiplier_w12} 
                    onChange={(e) => setMultipliers({...multipliers, multiplier_w12: parseFloat(e.target.value) || 0})}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">× Einheiten = Netto-Provision</span>
                </div>
                <p className="text-xs text-gray-400">Berechnung: Einheiten × {multipliers.multiplier_w12} × (1 + MwSt)</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>MwSt-Satz</span>
                  <span className="text-xs text-gray-400">Standard: 19%</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={multipliers.mwst_rate} 
                    onChange={(e) => setMultipliers({...multipliers, mwst_rate: parseFloat(e.target.value) || 0})}
                    className="w-24"
                    data-testid="mwst-input"
                  />
                  <span className="text-sm text-gray-500">= {(multipliers.mwst_rate * 100).toFixed(0)}%</span>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={saveMwstAsDefault}
                    onChange={(e) => setSaveMwstAsDefault(e.target.checked)}
                    data-testid="save-mwst-default-checkbox"
                    className="rounded border-gray-300"
                  />
                  Als Standard-USt für {trainers.find(t => t.id === selectedTrainer)?.name || 'diesen Trainer'} speichern (gilt für neue Kampagnen/Quartale)
                </label>
                {trainers.find(t => t.id === selectedTrainer)?.default_mwst_rate != null && (
                  <p className="text-[11px] text-gray-400" data-testid="trainer-default-mwst-info">
                    Aktueller Standard: {(trainers.find(t => t.id === selectedTrainer).default_mwst_rate * 100).toFixed(0)} % USt
                  </p>
                )}
              </div>
              
              <div className="space-y-2 border-t pt-3">
                <Label className="flex items-center justify-between">
                  <span>UG-Faktor (€ pro Einheit)</span>
                  <span className="text-xs text-gray-400">Standard: 2,00 €</span>
                </Label>
                <Input 
                  type="number"
                  step="0.1"
                  min="0"
                  value={multipliers.ug_factor ?? 2.0} 
                  onChange={(e) => setMultipliers({...multipliers, ug_factor: parseFloat(e.target.value) || 0})}
                  className="w-24"
                  data-testid="ug-factor-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>UG-USt-Satz</span>
                  <span className="text-xs text-gray-400">Standard: 19%</span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={multipliers.ug_mwst_rate ?? 0.19} 
                    onChange={(e) => setMultipliers({...multipliers, ug_mwst_rate: parseFloat(e.target.value) || 0})}
                    className="w-24"
                    data-testid="ug-mwst-input"
                  />
                  <span className="text-sm text-gray-500">= {((multipliers.ug_mwst_rate ?? 0.19) * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800">Beispielrechnung W1+2:</p>
              <p className="text-blue-600">10 Einheiten × {multipliers.multiplier_w12} × {(1 + multipliers.mwst_rate).toFixed(2)} = {(10 * multipliers.multiplier_w12 * (1 + multipliers.mwst_rate)).toFixed(2)} €</p>
              <p className="text-emerald-700 mt-1">UG-Anteil: 10 E × {(multipliers.ug_factor ?? 2.0)} € × {(1 + (multipliers.ug_mwst_rate ?? 0.19)).toFixed(2)} − Trainer-Provision</p>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowMultiplierDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={saveMultipliers} 
                disabled={savingMultipliers}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                {savingMultipliers ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProvisionManagerExtended;
